import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { CommerceBlockProps, COMMERCE_BLOCK_TITLES } from './commerceBlockEditors'
import { DataSourceRefreshEditor, RepeatLayoutExtras, RepeatSelectEditor, StyleRulesEditor, TimerEditor } from './LiveEditors'
import { Plus, Trash2, Sparkles, GripVertical } from 'lucide-react'
import {
  loadDashboardObject,
  loadPageObject,
  loadReportObject,
  loadTransactionObject,
  loadVisualObject,
  listProjectDashboards,
  listProjectPages,
  listProjectReports,
  listProjectVisuals,
  type ProjectDashboardSummary,
  type ProjectPageSummary,
  type ProjectReportSummary,
  type ProjectVisualSummary,
} from '@/api/designers'
import type { ColumnDef, TransactionModel } from '../transaction/types'
import type {
  ArgMapping,
  BindingExpr,
  Block,
  BlockStyle,
  BlockType,
  BreadcrumbBlock,
  BreadcrumbItem,
  DataFilterValueRef,
  DataSourceFilter,
  EmbedBlock,
  EmbedColumnTotal,
  EmbedGridUx,
  RuntimeDeletePolicy,
  EmbedFilter,
  EmbedObjType,
  FilterOp,
  FilterValueRef,
  InputGroupBlock,
  PageDataSource,
  PageInput,
  PageInputType,
  PageKind,
  PageModel,
  Section,
  Selection,
  ValidationRule,
  ValidationRuleKind,
} from './types'
import { PAGE_KIND_LABELS } from './types'
import { normalizeEmbedMode, type NavMenuItem, type NormalizedEmbedMode, type RichContentBlock, type CollectionSearchBlock, type CommentsBlock } from '@twasta/page-schema'
import { normalizePageKind } from './migrate'
import { useObjectVersion } from '@/store/objectVersionStore'
import { makeBlock, makeFilter, newId, BLOCK_OPTIONS } from './blocks'
import type { SiteMenuItem, SiteMenus } from './renderers'
import PageMenusPanel from './PageMenusPanel'
import { DEFAULT_COLOR_SCHEMES } from './tokens'
import { isBlockIdAvailable, findBlockById, updateBlockById, deleteBlockById, walkBlocks } from './pageOps'
import ArgMappingEditor, { type ArgTargetOption } from './ArgMappingEditor'
import SectionLayoutEditor from './SectionLayoutEditor'
import FormSubmissionsPanel from './FormSubmissionsPanel'
import OnClickEditor from './actions/OnClickEditor'
import BeforeLoadEditor from './BeforeLoadEditor'
import { extractVisualParams } from '../dashboard/visualParams'
import ColumnTotalEditor from './embedTable/ColumnTotalEditor'
import { columnDbName } from '@/livepreview/utils'
import ObjectPickerField, { type ObjectPickerEntry } from './ObjectPickerField'
import { listWizards, loadWizard, type WizardSummary } from '@/api/wizards'
import ThemeEditor from './ThemeEditor'
import { withThemeSchemes } from './ProjectThemePanel'
import BlockStyleEditor, { BackgroundEditor, BorderEditor, ColorRow } from './BlockStyleEditor'
import SizeAndPositionEditor, { SpacingEditor } from './SizeAndPositionEditor'
import { VisibilityEditor } from './VisibilityEditor'
import { AnimationEditor } from './AnimationEditor'
import { CustomCodeEditor } from './CustomCodeEditor'
import { FormBlockEditor } from './FormBlockEditor'
import { TableBlockEditor } from './TableBlockEditor'
import { ChatbotBlockEditor } from './ChatbotBlockEditor'
import { SectionPopupEditor } from './PopupBlockEditor'
import ActionEditor from './actions/ActionEditor'
import { BindingPicker } from './BindingPicker'
import { MARKETING_BLOCK_EDITORS, MARKETING_EDITOR_TITLES } from './marketingBlockEditors'
import { ValidationsEditor } from './ValidationsEditor'
import { FilterBarEditor, AuthFormEditor, DraftTextField } from './appBlockEditors'
import {
  HeroEditor, CtaBannerEditor, SpacerEditor, ColumnsEditor, ContainerEditor,
  CardEditor, CardGridEditor, FeatureGridEditor, StatsEditor, CountdownEditor, TestimonialEditor, TimelineEditor,
  PricingEditor, FaqEditor, TabsEditor, AccordionEditor, LogoCloudEditor,
  ImageEditor, VideoEditor, GalleryEditor, MapEditor, ButtonEditor, ButtonGroupEditor,
  NewsletterEditor, ContactFormEditor, CalloutEditor,
  EntityPickerEditor, NewCustomerFormEditor, ProductSelectionEditor,
  ProductPreviewEditor, OrderSummaryEditor, OrderSubmitEditor, PaymentCheckoutEditor, PaymentSettingsEditor, AuthMenuEditor,
  CartBadgeEditor, CartLinesEditor, CartSummaryEditor,
  WishlistButtonEditor, WishlistBadgeEditor, WishlistLinesEditor,
  FileUploadEditor,
} from './blockEditors'

interface Props {
  project: string
  page: PageModel
  selection: Selection
  transactions: { obj_name: string; title: string }[]
  // The resolved site navigation for this page's website (pages + nav
  // folders as groups). Lets the nav_menu editor offer a "menu root
  // folder" dropdown and seed a custom menu from the live structure.
  siteMenu?: SiteMenuItem[]
  // The website's NAMED menus (resolved) — the nav_menu editor's Menu picker.
  siteMenus?: SiteMenus
  // Website settings URL (where menus are created / ordered).
  siteEditUrl?: string
  // A page's menu membership changed — re-read the site's menus.
  onSiteMenusChanged?: () => void
  onPageChange: (next: PageModel) => void
  onSelectInput: (idx: number) => void
  // Required so renaming a block id from the property panel can also
  // update the active selection (the old id stops resolving the moment
  // the rename lands, otherwise the panel falls back to "Block not found"
  // until the user clicks elsewhere).
  onSelectionChange: (next: Selection) => void
  // Invoked when the user clicks "Edit transaction" on an embed block
  // targeting a transaction. The parent owns the unsaved-changes guard
  // and the React Router navigate call, so it builds the right return
  // state and hops to the transaction designer.
  onOpenTransaction?: (txnObjName: string) => void
  // Open the before-load access event's BL function in the code editor
  // (resolves/generates the file, opens the object's closeable Code tab).
  onOpenCode?: (method?: string) => void
}

// Muted helper line under a field — used by the Interaction group to
// explain what an anchor / tooltip / block click actually does.
const HELP_STYLE: React.CSSProperties = {
  margin: '6px 0 0', fontSize: '0.8125rem', lineHeight: 1.45,
  color: 'var(--color-text-secondary)',
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  display: 'block',
}

const MINI_LABEL_STYLE: React.CSSProperties = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 2,
  display: 'block',
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: '0.9125rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  color: 'var(--color-text)',
  outline: 'none',
}

const SECTION_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 14,
}

export default function PropertyPanel({
  project,
  page,
  selection,
  transactions,
  siteMenu,
  siteMenus,
  siteEditUrl,
  onSiteMenusChanged,
  onPageChange,
  onSelectInput,
  onSelectionChange,
  onOpenTransaction,
  onOpenCode,
}: Props) {
  if (selection.type === 'page') {
    return <PagePropertiesEditor page={page} project={project} transactions={transactions} onPageChange={onPageChange} onOpenCode={onOpenCode} siteEditUrl={siteEditUrl} onSiteMenusChanged={onSiteMenusChanged} />
  }
  if (selection.type === 'inputs' || selection.type === 'input') {
    // 'input' (a single-focused input) is folded into the unified inputs
    // manager; the user can collapse / expand any card from there.
    return <InputsEditor page={page} onPageChange={onPageChange} onSelectInput={onSelectInput} />
  }
  if (selection.type === 'section' && selection.sectionId) {
    return <SectionPropertiesEditor page={page} project={project} sectionId={selection.sectionId} onPageChange={onPageChange} />
  }
  if (selection.type === 'block' && selection.blockId) {
    // Recursive lookup — the block may live in Header/Footer or inside a
    // nested container (a tab body, an accordion item, a column, the
    // children of a Container/Card). updateBlockById and deleteBlockById
    // both walk the same recursive structure so renames and deletes keep
    // working at any depth.
    const block = findBlockById(page, selection.blockId)
    if (!block) return <EmptyState text="Block not found" />
    return (
      <BlockEditor
        // Remount per block: several sub-editors keep local drafts, and
        // without a key they carried one block's draft into the next.
        key={block.id}
        project={project}
        block={block}
        page={page}
        transactions={transactions}
        siteMenu={siteMenu}
        siteMenus={siteMenus}
        siteEditUrl={siteEditUrl}
        onOpenTransaction={onOpenTransaction}
        onChange={(next) => {
          // The block id can change here (the BlockIdEditor lets the user
          // rename it). updateBlockById matches the *current* id, so when
          // renaming we issue the patch under the old id; the recursive
          // helper merges the new id into the block via spread.
          onPageChange(updateBlockById(page, block.id, next))
          if (next.id !== block.id) {
            onSelectionChange({ type: 'block', blockId: next.id, sectionId: selection.sectionId })
          }
        }}
        onDelete={() => {
          onPageChange(deleteBlockById(page, block.id))
        }}
      />
    )
  }
  return <EmptyState text="Select an item to edit its properties" />
}

// ─── Section properties (when a section card is selected on the canvas) ─

/**
 * A section's own surface and spacing: its background fill and the padding
 * between that surface and its blocks. Left/right show "auto" when unset —
 * the renderers then inset a section that paints a surface by the page's
 * side padding (`sectionDefaultInset`); typing 0 makes content run edge to
 * edge.
 */
function SectionStyleFields({ section, project, onChange }: {
  section: Section
  project: string
  onChange: (style: BlockStyle | undefined) => void
}) {
  const style: BlockStyle = section.style || {}
  const patch = (next: Partial<BlockStyle>) => {
    const merged: BlockStyle = { ...style, ...next }
    for (const k of Object.keys(merged) as (keyof BlockStyle)[]) {
      if (merged[k] === undefined) delete merged[k]
    }
    onChange(Object.keys(merged).length ? merged : undefined)
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Background</label>
        <BackgroundEditor
          background={style.background}
          project={project}
          onChange={(background) => patch({ background })}
        />
      </div>
      <SpacingEditor
        label="Padding (px)"
        value={style.padding}
        placeholders={{ left: 'auto', right: 'auto' }}
        onChange={(padding) => patch({ padding })}
      />
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        Space between the section's edge and its blocks. Left/right left
        empty use the page's side margin automatically when the section has a
        background; enter 0 for edge-to-edge content.
      </p>
      {/* The rest of BlockStyle the section wrapper paints (blockStyleCss on
          every surface). Only background + padding were editable, so a
          section's text colour, frame or outer spacing had to be hand-edited. */}
      <SpacingEditor
        label="Margin (px)"
        value={style.margin}
        onChange={(margin) => patch({ margin })}
      />
      <div>
        <label style={FIELD_LABEL_STYLE}>Text colour</label>
        <ColorRow value={style.textColor || ''} placeholder="inherit" onChange={(c) => patch({ textColor: c || undefined })} />
      </div>
      <BorderEditor border={style.border} onChange={(border) => patch({ border })} />
      <div>
        <label style={FIELD_LABEL_STYLE}>Corner radius (px)</label>
        <input
          type="number" min={0}
          value={style.borderRadius ?? ''}
          placeholder="0"
          onChange={(e) => {
            const v = e.target.value.trim()
            patch({ borderRadius: v === '' ? undefined : Math.max(0, Number(v) || 0) })
          }}
          style={INPUT_STYLE}
        />
      </div>
    </div>
  )
}

function SectionPropertiesEditor({ page, project, sectionId, onPageChange }: { page: PageModel; project: string; sectionId: string; onPageChange: (p: PageModel) => void }) {
  const sec = page.sections?.find(s => s.id === sectionId)
  if (!sec) return <EmptyState text="Section not found" />
  const isMain = (sec.tag ?? 'main') === 'main'
  const set = (patch: Partial<typeof sec>) => {
    const sections = (page.sections ?? []).map(s => (s.id === sectionId ? { ...s, ...patch } : s))
    const main = sections.find(s => (s.tag ?? 'main') === 'main')
    onPageChange({ ...page, sections, blocks: main?.blocks ?? page.blocks })
  }
  const schemes = page.designTokens?.colorSchemes ?? DEFAULT_COLOR_SCHEMES
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 14, borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background:
              sec.tag === 'header' ? '#3b82f6' :
              sec.tag === 'footer' ? '#6366f1' :
              sec.tag === 'aside'  ? '#a855f7' :
              sec.tag === 'popup'  ? '#14b8a6' :
              isMain               ? '#10b981' : '#8b5cf6',
          }} />
          <strong style={{ fontSize: 14 }}>
            {sec.tag === 'header' ? 'Header' : sec.tag === 'footer' ? 'Footer' : sec.tag === 'aside' ? 'Aside' : sec.tag === 'popup' ? 'Pop-up' : isMain ? 'Main content' : (sec.name || 'Section')}
          </strong>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {isMain
            ? 'The body of your page. Add and edit blocks directly in the canvas.'
            : sec.tag === 'header'
              ? 'Sits at the top of every viewport. Use it for a logo, navigation, or banner.'
              : sec.tag === 'footer'
                ? 'Sits at the bottom. Use it for copyright, links, or contact info.'
                : sec.tag === 'popup'
                  ? 'Opens in a pop-up modal. Add blocks below (chatbot, settings page, …) — they render inside the dialog, not on the page.'
                  : 'A side region for promos, filters, or callouts.'}
        </p>
      </div>

      <div style={SECTION_STYLE}>
        {sec.tag === 'popup' && (
          <SectionPopupEditor
            value={sec.popup}
            onChange={(popup) => set({ popup })}
          />
        )}

        <div>
          <label style={FIELD_LABEL_STYLE}>Section name (your reference only)</label>
          <input
            value={sec.name ?? ''}
            onChange={e => set({ name: e.target.value })}
            disabled={isMain}
            style={{ ...INPUT_STYLE, opacity: isMain ? 0.6 : 1 }}
          />
        </div>

        {/* Tier-3 layout primitives: width, height, dividers, decorations,
            sticky, anchor. Hidden for pop-up sections, whose geometry is the
            modal's, not the page's. */}
        {sec.tag !== 'popup' && (
          <SectionLayoutEditor section={sec} onChange={set} />
        )}

        {sec.tag !== 'popup' && (
          <SectionStyleFields section={sec} project={project} onChange={(style) => set({ style })} />
        )}

        <div>
          <label style={FIELD_LABEL_STYLE}>Block spacing (px)</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              max={120}
              placeholder={String(page.theme?.layout?.blockGap ?? 14)}
              value={sec.blockGap ?? ''}
              onChange={e => {
                const raw = e.target.value
                if (raw === '') return set({ blockGap: undefined })
                const n = Math.max(0, Math.min(120, parseInt(raw, 10) || 0))
                set({ blockGap: n })
              }}
              style={{ ...INPUT_STYLE, flex: 1 }}
            />
            {typeof sec.blockGap === 'number' && (
              <button
                type="button"
                onClick={() => set({ blockGap: undefined })}
                title="Reset to page default"
                style={{
                  padding: '6px 10px', fontSize: 13, cursor: 'pointer',
                  background: 'var(--color-bg)', color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                }}
              >
                Reset
              </button>
            )}
          </div>
          <p style={{
            margin: '6px 0 0', fontSize: '0.8125rem',
            color: 'var(--color-text-secondary)', lineHeight: 1.4,
          }}>
            Vertical space between blocks in this section. Leave blank to inherit the page default
            ({page.theme?.layout?.blockGap ?? 14}px).
          </p>
        </div>

        <div>
          <label style={FIELD_LABEL_STYLE}>Background color scheme</label>
          {schemes.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0 }}>
              No color schemes defined yet. Open the toolbar's <strong>Advanced → Color schemes</strong> tab to add one.
            </p>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              <button
                type="button"
                onClick={() => set({ colorSchemeId: undefined })}
                title="Inherit page theme"
                style={{
                  width: 36, height: 36, borderRadius: 6,
                  border: !sec.colorSchemeId
                    ? '2px solid var(--color-primary)'
                    : '1px solid var(--color-border)',
                  background: 'repeating-linear-gradient(45deg, #fff, #fff 4px, #e5e7eb 4px, #e5e7eb 8px)',
                  cursor: 'pointer', padding: 0, fontSize: 13, color: '#888',
                }}
              >
                Inherit
              </button>
              {schemes.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => set({ colorSchemeId: s.id })}
                  title={s.name}
                  style={{
                    width: 36, height: 36, borderRadius: 6,
                    border: sec.colorSchemeId === s.id
                      ? '2px solid var(--color-primary)'
                      : '1px solid var(--color-border)',
                    background: s.background,
                    cursor: 'pointer',
                    position: 'relative', padding: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', bottom: 2, right: 2,
                    width: 12, height: 12, borderRadius: '50%',
                    background: s.primary,
                    border: '1px solid rgba(0,0,0,0.1)',
                  }} />
                </button>
              ))}
            </div>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 14, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={sec.lazy ?? false}
            onChange={e => set({ lazy: e.target.checked })}
            style={{ marginTop: 2 }}
          />
          <span>
            <span style={{ fontWeight: 500 }}>Lazy load</span>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Defer rendering until the user scrolls here. Good for big sections far down the page.
            </div>
          </span>
        </label>
      </div>
      {/* Device / role / schedule / condition gating — the same editor a
          block uses; every surface honours section.visibility. */}
      <CollapsibleSection title="Visibility" defaultOpen={!!sec.visibility}>
        <VisibilityEditor value={sec.visibility} onChange={(visibility) => set({ visibility })} />
      </CollapsibleSection>
    </div>
  )
}

// ─── Page meta ─────────────────────────────────────────

function PagePropertiesEditor({ page, project, transactions, onPageChange, onOpenCode, siteEditUrl, onSiteMenusChanged }: { page: PageModel; project: string; transactions: { obj_name: string; title: string }[]; onPageChange: (p: PageModel) => void; onOpenCode?: (method?: string) => void; siteEditUrl?: string; onSiteMenusChanged?: () => void }) {
  const [tab, setTab] = useState<'props' | 'theme'>('props')
  const inputVarHint = page.inputs.length > 0
    ? ` · ${page.inputs.slice(0, 3).map((i) => `{{${i.name}}}`).join(' · ')}${page.inputs.length > 3 ? ' …' : ''}`
    : ''

  if (tab === 'theme') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <PropertyTabs tab={tab} setTab={setTab} />
        <div style={{ padding: 14 }}>
          <ThemeEditor
            theme={page.theme}
            project={project}
            onChange={(theme) => onPageChange({ ...page, theme })}
            onBindTheme={(theme, schemes) => onPageChange({
              ...page, theme, designTokens: withThemeSchemes(page.designTokens, schemes),
            })}
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <PropertyTabs tab={tab} setTab={setTab} />
      <div style={SECTION_STYLE}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Title</label>
        <input
          value={page.title}
          onChange={(e) => onPageChange({ ...page, title: e.target.value })}
          style={INPUT_STYLE}
          placeholder="Untitled Page"
        />
        <p style={{
          margin: '6px 0 0', fontSize: '0.8125rem',
          color: 'var(--color-text-secondary)', lineHeight: 1.4,
        }}>
          The title is not auto-rendered on the page. To show it, add a Heading
          block (or any text field) and use the <code style={{ fontFamily: 'monospace' }}>{'{{title}}'}</code> variable.
        </p>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Description</label>
        <textarea
          value={page.description || ''}
          onChange={(e) => onPageChange({ ...page, description: e.target.value })}
          rows={3}
          style={{ ...INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Object Name</label>
        <input value={page.obj_name} disabled style={{ ...INPUT_STYLE, opacity: 0.7 }} />
      </div>
      <div style={{
        padding: '8px 10px',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        lineHeight: 1.5,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--color-text)' }}>Variables</div>
        Use these tokens in any block text and they render at runtime:
        <div style={{ marginTop: 4, fontFamily: 'monospace' }}>
          {'{{title}}'} · {'{{description}}'}{inputVarHint}
        </div>
        {(page.actions || []).length > 0 && (
          <div style={{ marginTop: 6 }}>
            And from any defined action:
            <div style={{ marginTop: 2, fontFamily: 'monospace' }}>
              {'{{<action>.loading}}'} · {'{{<action>.error}}'} · {'{{<save_key>.dotted.path}}'}
            </div>
          </div>
        )}
      </div>

      {/* Page kind — set at creation, immutable; document-only fields below */}
      <PageKindPanel page={page} onPageChange={onPageChange} />

      {/* Which of its website's menus this page is listed in. */}
      {normalizePageKind(page).kind === 'external' && (
        <PageMenusPanel project={project} pageObjName={page.obj_name} editUrl={siteEditUrl} onChanged={onSiteMenusChanged} />
      )}

      {/* Page actions — declarative click handlers referenced by buttons */}
      <div style={{ marginTop: 4 }}>
        <ActionEditor
          actions={page.actions || []}
          inputs={page.inputs}
          project={project}
          transactions={transactions}
          dataSources={(page.dataSources || []).map((d) => d.id)}
          onChange={(next) => onPageChange({ ...page, actions: next })}
        />
      </div>

      {/* Transaction data sources — bind blocks to live object data */}
      <div style={{ marginTop: 4 }}>
        <DataSourcesEditor page={page} transactions={transactions} onPageChange={onPageChange} />
      </div>

      {/* Before-load access event — server-side gate run before the page renders */}
      <div style={{ marginTop: 4 }}>
        <BeforeLoadEditor page={page} project={project} onPageChange={onPageChange} onOpenCode={onOpenCode} />
      </div>

      {/* Submissions inbox — only when the page actually has a form, so it
          doesn't take up room on a page that can't collect anything. */}
      {pageHasForm(page) && (
        <div style={{ marginTop: 4, borderTop: '1px solid var(--color-border)' }}>
          <FormSubmissionsPanel
            project={project}
            pageObjName={page.obj_name}
          />
        </div>
      )}
      </div>
    </div>
  )
}

/** True when the page contains any block that collects a submission. */
function pageHasForm(page: PageModel): boolean {
  const FORM_TYPES = new Set(['form', 'contact_form', 'newsletter'])
  let found = false
  // `walkBlocks` is the canonical recursion — it resolves a block's child
  // slots by TYPE. Reaching for `block.columns` generically would crash on a
  // card_grid, where `columns` is a column COUNT, not an array of columns.
  const visit = (b: Block) => { if (FORM_TYPES.has(b.type)) found = true }
  for (const sec of page.sections ?? []) walkBlocks(sec.blocks ?? [], visit)
  if (!page.sections?.length) walkBlocks(page.blocks ?? [], visit)
  return found
}

// Map a block to its updated form when switching the page into document
// mode. Auto-marks every applicable block as documentEditable so the
// author starts from "everything is editable, narrow it down" rather
// than having to click every block. Already-explicit values are left
// alone — re-entering document mode never overwrites a deliberate toggle.
//
// Recurses into container slots (children + tabs/accordion items +
// columns) so nested blocks get the same default treatment.
function autoMarkBlockEditable(block: Block): Block {
  let next: Block = block
  if (isDocumentApplicable(block) && block.documentEditable === undefined) {
    next = { ...next, documentEditable: true }
  }
  // Recurse into per-container-type slots.
  if (next.type === 'tabs') {
    const items = next.items.map(it =>
      it.blocks && it.blocks.length > 0 ? { ...it, blocks: it.blocks.map(autoMarkBlockEditable) } : it,
    )
    next = { ...next, items } as Block
  } else if (next.type === 'accordion') {
    const items = next.items.map(it =>
      it.blocks && it.blocks.length > 0 ? { ...it, blocks: it.blocks.map(autoMarkBlockEditable) } : it,
    )
    next = { ...next, items } as Block
  } else if (next.type === 'columns') {
    const columns = next.columns.map(c =>
      c.blocks && c.blocks.length > 0 ? { ...c, blocks: c.blocks.map(autoMarkBlockEditable) } : c,
    )
    next = { ...next, columns } as Block
  } else if (next.children && next.children.length > 0) {
    next = { ...next, children: next.children.map(autoMarkBlockEditable) } as Block
  }
  return next
}

// Exported for the New Page wizard, which calls this once when seeding a
// `kind='document'` page so every applicable block starts editable
// without the author needing to flip each one by hand. The previous in-
// place toggle invoked this on transition to document mode; with kind
// now immutable, this only runs at creation.
export function applyAutoEditableToPage(page: PageModel): PageModel {
  const sections = page.sections
  if (sections && sections.length > 0) {
    const mappedSections = sections.map(s => ({
      ...s,
      blocks: (s.blocks ?? []).map(autoMarkBlockEditable),
    }))
    // Mirror the main section's blocks into the legacy top-level
    // `page.blocks` so ensureMainSection (called in handlePageChange)
    // doesn't push stale legacy blocks back over our updates.
    const main = mappedSections.find(s => (s.tag ?? 'main') === 'main')
    return { ...page, sections: mappedSections, blocks: main?.blocks ?? page.blocks }
  }
  // Legacy single-list pages.
  return { ...page, blocks: (page.blocks ?? []).map(autoMarkBlockEditable) }
}

// Editable kind picker + kind-specific fields. The backend's
// save-page-object endpoint accepts kind transitions (see comment in
// `routers/designers.py::save_page_object`), so authors can flip an
// existing page between document / internal / external. Switching TO
// document additionally auto-marks applicable blocks editable, matching
// the New Page wizard's seed behaviour for `kind='document'`.
function PageKindPanel({ page, onPageChange }: { page: PageModel; onPageChange: (p: PageModel) => void }) {
  const kind: PageKind = normalizePageKind(page).kind ?? 'internal'

  // Walk every block in every section so the title-block dropdown can
  // surface every text-bearing block, regardless of where it lives.
  const allBlocks: Block[] = []
  const sections = page.sections ?? []
  if (sections.length === 0) allBlocks.push(...(page.blocks ?? []))
  else for (const s of sections) allBlocks.push(...(s.blocks ?? []))

  const titleEligible = allBlocks.filter(b =>
    b.type === 'heading' || b.type === 'paragraph'
  )

  const tileDescriptions: Record<PageKind, string> = {
    internal: 'Login-gated page in your Vision app.',
    external: 'Public page on your tenant subdomain or custom domain.',
    document: 'Notion-style template that spawns document instances.',
  }
  const longDescriptions: Record<PageKind, string> = {
    internal: 'Login-gated page rendered inside the Vision app.',
    external: 'Public page deployed to your tenant subdomain (and any custom domains).',
    document: "Each runtime visit spawns a Notion-style document instance. Open a block's Properties → Document mode to mark it editable.",
  }

  const handleKindChange = (next: PageKind) => {
    if (next === kind) return
    // Mirror `pageMode` for legacy readers (matches normalizePageKind):
    // 'document' → 'document'; everything else → 'web'.
    const nextPageMode = next === 'document' ? 'document' : 'web'
    const updated: PageModel = { ...page, kind: next, pageMode: nextPageMode }
    // Switching INTO document prompts the author to decide whether every
    // applicable block should start as editable — same UX as the prior
    // pageMode toggle that the external-website refactor inadvertently
    // dropped. Confirm → auto-mark all; cancel → switch with no changes
    // so deliberate per-block toggles aren't clobbered.
    if (next === 'document' && kind !== 'document') {
      const makeEditable = window.confirm(
        'Switch to Smart Document?\n\nMark all applicable blocks as editable so each runtime visit can spawn a document instance?\n\nOK = mark all editable, Cancel = switch without auto-marking.'
      )
      onPageChange(makeEditable ? applyAutoEditableToPage(updated) : updated)
      return
    }
    onPageChange(updated)
  }

  const tiles: PageKind[] = ['internal', 'external', 'document']

  return (
    <div style={{
      marginTop: 8,
      padding: 10,
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      background: 'var(--color-bg)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: 8 }}>
        Page kind
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
        {tiles.map((k) => {
          const selected = kind === k
          return (
            <button
              key={k}
              type="button"
              onClick={() => handleKindChange(k)}
              title={tileDescriptions[k]}
              style={{
                textAlign: 'left',
                padding: '8px 10px',
                border: '1px solid ' + (selected ? 'var(--color-accent)' : 'var(--color-border)'),
                borderRadius: 6,
                background: selected ? 'var(--color-accent)' : 'var(--color-bg)',
                color: selected ? 'var(--pg-on-primary, #fff)' : 'var(--color-text)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                minHeight: 64,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {PAGE_KIND_LABELS[k]}
              </span>
              <span style={{ fontSize: 13, lineHeight: 1.3, opacity: 0.85 }}>
                {tileDescriptions[k]}
              </span>
            </button>
          )
        })}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        {longDescriptions[kind]}
      </p>

      {kind === 'document' && (
        <div style={{ marginTop: 12 }}>
          <label style={FIELD_LABEL_STYLE}>Title block (optional)</label>
          <select
            value={page.documentDefaults?.titleBlockId ?? ''}
            onChange={(e) => onPageChange({
              ...page,
              documentDefaults: { ...(page.documentDefaults ?? {}), titleBlockId: e.target.value || undefined },
            })}
            style={INPUT_STYLE}
          >
            <option value="">— None —</option>
            {titleEligible.map(b => (
              <option key={b.id} value={b.id}>{b.id} ({b.type})</option>
            ))}
          </select>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
            When set, the document's title is seeded from this block on creation.
          </p>

          <RuntimeAddableEditor page={page} onPageChange={onPageChange} />
          <RuntimeDeletableEditor page={page} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  )
}

// ── Page-level "delete policy" editor ─────────────────────────────────
// Five-level policy that decides which blocks the runtime customer can
// remove from their document instance. Companion to RuntimeAddableEditor:
// add governs growth, delete governs trimming.
function RuntimeDeletableEditor({
  page,
  onPageChange,
}: {
  page: PageModel
  onPageChange: (p: PageModel) => void
}) {
  // Default-fallback to 'added_only' when unset — matches the existing
  // runtime behaviour so legacy pages keep their current UX.
  const policy: RuntimeDeletePolicy = page.runtimeDeletable ?? 'added_only'
  const setPolicy = (next: RuntimeDeletePolicy) => {
    onPageChange({ ...page, runtimeDeletable: next })
  }
  const options: { value: RuntimeDeletePolicy; label: string; description: string }[] = [
    { value: 'none',        label: 'No deletion',          description: 'Document is read-shape — no block can be removed.' },
    { value: 'added_only',  label: 'Runtime-added only',   description: 'Default. Customer can only delete blocks they added themselves.' },
    { value: 'marked',      label: 'Added + marked',       description: 'Runtime-added plus template blocks each marked deletable below.' },
    { value: 'empty',       label: 'Empty blocks',         description: 'Any block whose content is empty (no text / items / url) plus runtime-added.' },
    { value: 'any',         label: 'Any block',            description: 'Most permissive — every block (template or added) can be deleted.' },
  ]

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--color-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Allow runtime deletion
      </div>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
        Controls which blocks the customer can remove inside a Smart Document instance.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {options.map(o => {
          const selected = policy === o.value
          return (
            <label
              key={o.value}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid ' + (selected ? 'var(--color-accent)' : 'var(--color-border)'),
                background: selected ? 'rgba(0,112,243,0.06)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="rtd-policy"
                checked={selected}
                onChange={() => setPolicy(o.value)}
                style={{ marginTop: 2 }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{o.label}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                  {o.description}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

// ── Page-level "allow runtime users to add blocks" editor ─────────────
// Defaults to ON for any document-mode page that hasn't explicitly set
// the flag (matches the answer "allow all should be default unless the
// designer narrows it"). Whitelist mode is opt-in: leaving the radio on
// "Allow all" keeps `runtimeAddableTypes` undefined and the runtime
// picker shows every block type.
function RuntimeAddableEditor({
  page,
  onPageChange,
}: {
  page: PageModel
  onPageChange: (p: PageModel) => void
}) {
  const enabled = page.runtimeAddable !== false  // default-true
  const whitelist = Array.isArray(page.runtimeAddableTypes) ? page.runtimeAddableTypes : null
  const mode: 'all' | 'specific' = whitelist && whitelist.length > 0 ? 'specific' : 'all'

  const setEnabled = (next: boolean) => {
    onPageChange({ ...page, runtimeAddable: next })
  }
  const setMode = (next: 'all' | 'specific') => {
    if (next === 'all') {
      onPageChange({ ...page, runtimeAddableTypes: undefined })
    } else {
      // Seed with a sensible starter set the author can edit.
      const seed: BlockType[] = ['paragraph', 'heading', 'bullet_list', 'numbered_list', 'todo', 'image']
      onPageChange({ ...page, runtimeAddableTypes: seed })
    }
  }
  const toggleType = (t: BlockType) => {
    const cur = whitelist ?? []
    const has = cur.includes(t)
    const next = has ? cur.filter(x => x !== t) : [...cur, t]
    onPageChange({ ...page, runtimeAddableTypes: next })
  }

  // Group BLOCK_OPTIONS by category for the chip-grid.
  const grouped = useMemo(() => {
    const out: Record<string, typeof BLOCK_OPTIONS> = {}
    for (const opt of BLOCK_OPTIONS) {
      ;(out[opt.category] ??= []).push(opt)
    }
    return out
  }, [])

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--color-border)' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Allow users to add blocks at runtime</span>
      </label>
      <p style={{ margin: '4px 0 0 22px', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
        When on, viewers with edit permission see an <strong>Add block</strong> button at the
        end of the document and inside any container marked <em>runtime-addable</em>.
      </p>

      {enabled && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
            Allowed block types
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 13, marginBottom: 8 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="rta-mode" checked={mode === 'all'} onChange={() => setMode('all')} />
              <span>Allow all (default)</span>
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="rta-mode" checked={mode === 'specific'} onChange={() => setMode('specific')} />
              <span>Specific types</span>
            </label>
          </div>

          {mode === 'specific' && (
            <div
              style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                maxHeight: 240, overflowY: 'auto',
                padding: 8, borderRadius: 6, border: '1px solid var(--color-border)',
                background: 'var(--color-surface, transparent)',
              }}
            >
              {Object.entries(grouped).map(([cat, opts]) => (
                <div key={cat}>
                  <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                    {cat}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {opts.map(opt => {
                      const on = (whitelist ?? []).includes(opt.type)
                      return (
                        <button
                          key={opt.type}
                          type="button"
                          onClick={() => toggleType(opt.type)}
                          title={opt.description}
                          style={{
                            fontSize: 13, padding: '3px 8px',
                            borderRadius: 999,
                            border: '1px solid ' + (on ? 'var(--color-accent)' : 'var(--color-border)'),
                            background: on ? 'var(--color-accent)' : 'transparent',
                            color: on ? 'var(--pg-on-primary, #fff)' : 'var(--color-text)',
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ marginRight: 4 }}>{opt.icon}</span>{opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
              {(whitelist?.length ?? 0) === 0 && (
                <p style={{ margin: 0, fontSize: 13, color: '#b91c1c' }}>
                  Pick at least one block type, or switch back to <em>Allow all</em>.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PropertyTabs({
  tab,
  setTab,
}: {
  tab: 'props' | 'theme'
  setTab: (t: 'props' | 'theme') => void
}) {
  const TAB: React.CSSProperties = {
    flex: 1, padding: '10px 12px', fontSize: '0.8425rem', fontWeight: 600,
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    borderBottom: '2px solid transparent',
  }
  const active: React.CSSProperties = {
    color: 'var(--color-text)',
    borderBottom: '2px solid var(--color-accent)',
  }
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)' }}>
      <button style={{ ...TAB, ...(tab === 'props' ? active : {}) }} onClick={() => setTab('props')}>
        Properties
      </button>
      <button style={{ ...TAB, ...(tab === 'theme' ? active : {}) }} onClick={() => setTab('theme')}>
        Theme
      </button>
    </div>
  )
}

// ─── Inputs list editor ─────────────────────────────────

function InputsEditor({
  page,
  onPageChange,
  onSelectInput,
}: {
  page: PageModel
  onPageChange: (p: PageModel) => void
  onSelectInput: (idx: number) => void
}) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)

  const addInput = () => {
    const base: PageInput = {
      name: `var_${page.inputs.length + 1}`,
      label: `Variable ${page.inputs.length + 1}`,
      type: 'text',                 // only consulted when Allow user input is on
      isInput: true,
      default: '',
    }
    onPageChange({ ...page, inputs: [...page.inputs, base] })
    setOpenIdx(page.inputs.length)
  }
  const removeInput = (idx: number) => {
    onPageChange({ ...page, inputs: page.inputs.filter((_, i) => i !== idx) })
    setOpenIdx(o => (o === idx ? null : o !== null && o > idx ? o - 1 : o))
  }
  const moveInput = (idx: number, delta: -1 | 1) => {
    const to = idx + delta
    if (to < 0 || to >= page.inputs.length) return
    const next = [...page.inputs]
    ;[next[idx], next[to]] = [next[to], next[idx]]
    onPageChange({ ...page, inputs: next })
    setOpenIdx(o => (o === idx ? to : o === to ? idx : o))
  }
  const update = (idx: number, patch: Partial<PageInput>) => {
    const next = [...page.inputs]
    next[idx] = { ...next[idx], ...patch }
    onPageChange({ ...page, inputs: next })
  }

  // Suppress unused warning — kept for compatibility with the parent's prop.
  void onSelectInput

  return (
    <div style={SECTION_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={sectionHeader}>Variables</h3>
        <button onClick={addInput} className="btn btn-primary btn-sm" title="Add variable">
          <Plus size={14} /> Add variable
        </button>
      </div>

      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        Variables hold values used by the page. Reference any variable as <code>{'{{name}}'}</code> in any block.
        Each variable can be filled by user input, an API call, a computed expression, or any combination.
      </p>

      {page.inputs.length === 0 && (
        <div style={{
          fontSize: 13, color: 'var(--color-text-secondary)',
          padding: 12, textAlign: 'center',
          border: '1px dashed var(--color-border)', borderRadius: 6,
          background: 'var(--color-bg-secondary)',
        }}>
          No variables yet — click <strong>Add variable</strong>.
        </div>
      )}

      {page.inputs.map((inp, i) => (
        <InputCard
          key={i}
          inp={inp}
          index={i}
          total={page.inputs.length}
          open={openIdx === i}
          allVars={page.inputs}
          onToggle={() => setOpenIdx(o => (o === i ? null : i))}
          onPatch={(patch) => update(i, patch)}
          onRemove={() => removeInput(i)}
          onMoveUp={() => moveInput(i, -1)}
          onMoveDown={() => moveInput(i, 1)}
        />
      ))}
    </div>
  )
}

const TYPE_LABELS: Record<PageInputType, string> = {
  text:        'Text',
  textarea:    'Multi-line text',
  number:      'Number',
  spinner:     'Spinner (± stepper)',
  percentage:  'Percentage (0–100)',
  slider:      'Slider',
  range:       'Range (low–high)',
  date:        'Date',
  datetime:    'Date + time',
  time:        'Time',
  date_range:  'Date range',
  checkbox:    'Checkbox',
  select:      'Dropdown',
  json:        'JSON (object / array)',
}
const NUMERIC_TYPES = new Set<PageInputType>(['number', 'spinner', 'percentage', 'slider', 'range'])

function InputCard({
  inp, index, total, open, allVars,
  onToggle, onPatch, onRemove, onMoveUp, onMoveDown,
}: {
  inp: PageInput; index: number; total: number; open: boolean
  allVars: PageInput[]
  onToggle: () => void
  onPatch: (patch: Partial<PageInput>) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 6,
      background: 'var(--color-bg)', overflow: 'hidden',
    }}>
      <header
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
          cursor: 'pointer', userSelect: 'none',
          background: open ? 'var(--color-bg-secondary)' : 'transparent',
        }}
      >
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13, width: 12 }}>
          {open ? '▾' : '▸'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {inp.label || inp.name || 'Untitled'}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
              {inp.name}
            </span>
            <SourceBadges inp={inp} />
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onMoveUp() }}   disabled={index === 0}        className="btn btn-ghost btn-sm" style={{ padding: 4 }} title="Move up">↑</button>
        <button onClick={(e) => { e.stopPropagation(); onMoveDown() }} disabled={index === total - 1} className="btn btn-ghost btn-sm" style={{ padding: 4 }} title="Move down">↓</button>
        <button onClick={(e) => { e.stopPropagation(); onRemove() }} className="btn btn-ghost btn-sm" title="Remove" style={{ color: 'var(--color-error)', padding: 4 }}>
          <Trash2 size={12} />
        </button>
      </header>

      {open && <SingleInputBody inp={inp} onPatch={onPatch} allVars={allVars} />}
    </div>
  )
}

function SourceBadges({ inp }: { inp: PageInput }) {
  const isInput = inp.isInput ?? (inp.display ? false : true)
  const hasApi = !!inp.api
  const hasExpr = inp.expression !== undefined && inp.expression !== ''
  const badges: Array<{ key: string; label: string; bg: string; fg: string }> = []
  if (isInput)  badges.push({ key: 'input',    label: 'Input',    bg: '#dbeafe', fg: '#1e40af' })
  if (hasApi)   badges.push({ key: 'api',      label: 'API',      bg: '#dcfce7', fg: '#166534' })
  if (hasExpr)  badges.push({ key: 'computed', label: 'Computed', bg: '#fef3c7', fg: '#92400e' })
  if (inp.display) badges.push({ key: 'display', label: 'Display', bg: '#f3e8ff', fg: '#6b21a8' })
  if (badges.length === 0) {
    badges.push({ key: 'static', label: 'Static', bg: 'var(--color-bg-secondary)', fg: 'var(--color-text-secondary)' })
  }
  return (
    <>
      {badges.map(b => (
        <span
          key={b.key}
          style={{
            display: 'inline-block', padding: '1px 6px', fontSize: 13, fontWeight: 600,
            background: b.bg, color: b.fg, borderRadius: 3,
            textTransform: 'uppercase', letterSpacing: '0.04em',
          }}
        >{b.label}</span>
      ))}
    </>
  )
}

function SingleInputBody({
  inp, onPatch, allVars,
}: {
  inp: PageInput
  onPatch: (patch: Partial<PageInput>) => void
  allVars: PageInput[]
}) {
  // Source flags — independent, can coexist. `hasExpr` tracks whether the
  // section is enabled (field present), not whether the expression is
  // non-empty — otherwise toggling on with an empty string would
  // immediately appear off.
  const isInput = inp.isInput ?? (inp.display ? false : true)
  const hasApi = !!inp.api
  const hasExpr = inp.expression !== undefined
  const isNumeric = isInput && NUMERIC_TYPES.has(inp.type)
  const isRange   = isInput && (inp.type === 'range' || inp.type === 'date_range')

  return (
    <div style={{ padding: 12, display: 'grid', gap: 12, borderTop: '1px solid var(--color-border)' }}>
      {/* ── Identity ──────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Label</label>
          <input value={inp.label} onChange={(e) => onPatch({ label: e.target.value })} style={INPUT_STYLE} />
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Identifier (<code>{'{{name}}'}</code>)</label>
          <input
            value={inp.name}
            onChange={(e) => onPatch({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })}
            style={{ ...INPUT_STYLE, fontFamily: 'monospace' }}
          />
        </div>
      </div>

      {/* ── Variable type (always editable) ─────────────── */}
      <div>
        <label style={FIELD_LABEL_STYLE}>Type</label>
        <select value={inp.type} onChange={(e) => onPatch({ type: e.target.value as PageInputType })} style={INPUT_STYLE}>
          {(Object.keys(TYPE_LABELS) as PageInputType[]).map(t => (
            <option key={t} value={t}>{TYPE_LABELS[t]}</option>
          ))}
        </select>
        {inp.type === 'json' && (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
            Structured value. Populate from an API (omit <code>pickPath</code> to
            store the full response) or set a JSON literal default. Consume from
            blocks via <code>{'{{name.path.to.field}}'}</code> tokens or by binding
            a block setting to this variable.
          </div>
        )}
      </div>

      {/* ── Initial / static default value ─────────────── */}
      <div>
        <label style={FIELD_LABEL_STYLE}>Default value</label>
        {inp.type === 'json' ? (
          <JsonDefaultEditor
            value={inp.default}
            onChange={(next) => onPatch({ default: next })}
          />
        ) : (
          <input
            value={inp.default ?? ''}
            onChange={(e) => onPatch({ default: e.target.value })}
            placeholder="Initial value used until something else writes"
            style={INPUT_STYLE}
          />
        )}
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Last writer wins. The default seeds the variable; the user, an API response, or a computed expression can each replace it.
        </div>
      </div>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      {/* ── Section: Form input (suppressed for JSON variables) ─────── */}
      {inp.type !== 'json' && (
      <SourceToggle
        label="Allow user input"
        description="Render as a form control. The user's typed value overrides any default."
        enabled={isInput}
        onToggle={(on) => onPatch({ isInput: on, display: on ? undefined : (inp.display ?? true) })}
      >
        {isRange && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={FIELD_LABEL_STYLE}>Default {inp.type === 'date_range' ? 'start' : 'low'}</label>
              <input value={inp.default || ''} onChange={(e) => onPatch({ default: e.target.value })} style={INPUT_STYLE} />
            </div>
            <div>
              <label style={FIELD_LABEL_STYLE}>Default {inp.type === 'date_range' ? 'end' : 'high'}</label>
              <input value={inp.default_to || ''} onChange={(e) => onPatch({ default_to: e.target.value })} style={INPUT_STYLE} />
            </div>
          </div>
        )}
        {isNumeric && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={FIELD_LABEL_STYLE}>Min</label>
              <input type="number" value={inp.min ?? ''} onChange={(e) => onPatch({ min: e.target.value === '' ? undefined : Number(e.target.value) })} style={INPUT_STYLE} />
            </div>
            <div>
              <label style={FIELD_LABEL_STYLE}>Max</label>
              <input type="number" value={inp.max ?? ''} onChange={(e) => onPatch({ max: e.target.value === '' ? undefined : Number(e.target.value) })} style={INPUT_STYLE} />
            </div>
            <div>
              <label style={FIELD_LABEL_STYLE}>Step</label>
              <input type="number" value={inp.step ?? ''} onChange={(e) => onPatch({ step: e.target.value === '' ? undefined : Number(e.target.value) })} style={INPUT_STYLE} />
            </div>
          </div>
        )}
        {(inp.type === 'number' || inp.type === 'spinner' || inp.type === 'slider' || inp.type === 'range') && (
          <div>
            <label style={FIELD_LABEL_STYLE}>Unit suffix (optional)</label>
            <input value={inp.unit ?? ''} onChange={(e) => onPatch({ unit: e.target.value || undefined })} placeholder="kg, ₹, mm" style={INPUT_STYLE} />
          </div>
        )}
        {(inp.type === 'text' || inp.type === 'textarea' || inp.type === 'number' || inp.type === 'spinner' || inp.type === 'percentage') && (
          <div>
            <label style={FIELD_LABEL_STYLE}>Placeholder</label>
            <input value={inp.placeholder ?? ''} onChange={(e) => onPatch({ placeholder: e.target.value })} style={INPUT_STYLE} />
          </div>
        )}
        {inp.type === 'select' && (
          <SelectOptionsEditor
            options={inp.options}
            onChange={(options) => onPatch({ options })}
          />
        )}
        <ValidationsEditor
          rules={inp.validations || []}
          onChange={(rules) => onPatch({ validations: rules.length ? rules : undefined })}
        />
      </SourceToggle>
      )}

      {/* ── Section: Auto-fetch from API ─────────────── */}
      <SourceToggle
        label="Auto-fetch from API"
        description="Populates the variable from a REST endpoint. Re-fires when configured triggers change."
        enabled={hasApi}
        onToggle={(on) => onPatch({ api: on ? (inp.api ?? { method: 'GET', url: '/api/', autoload: true }) : undefined })}
      >
        <ApiSourceEditor
          source={inp.api ?? { method: 'GET', url: '' }}
          allVars={allVars.filter(v => v.name !== inp.name)}
          onChange={(api) => onPatch({ api })}
        />
      </SourceToggle>

      {/* ── Section: From the page cart ─────────────── */}
      <SourceToggle
        label="Fill from the page cart"
        description="Turns the visitor's cart lines into detail rows of the transaction this variable feeds (one row per line: SKU + quantity)."
        enabled={!!inp.cart}
        onToggle={(on) => onPatch({ cart: on ? (inp.cart ?? { detailFormNo: '2', skuColumn: '', qtyColumn: '' }) : undefined })}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1fr', gap: 6 }}>
          <div>
            <label style={MINI_LABEL_STYLE}>Detail form</label>
            <input value={inp.cart?.detailFormNo ?? ''} placeholder="2"
              onChange={(e) => onPatch({ cart: { ...(inp.cart as NonNullable<PageInput['cart']>), detailFormNo: e.target.value } })}
              style={INPUT_STYLE} />
          </div>
          <div>
            <label style={MINI_LABEL_STYLE}>SKU column</label>
            <input value={inp.cart?.skuColumn ?? ''} placeholder="ITEM_CODE"
              onChange={(e) => onPatch({ cart: { ...(inp.cart as NonNullable<PageInput['cart']>), skuColumn: e.target.value } })}
              style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
          </div>
          <div>
            <label style={MINI_LABEL_STYLE}>Quantity column</label>
            <input value={inp.cart?.qtyColumn ?? ''} placeholder="QTY"
              onChange={(e) => onPatch({ cart: { ...(inp.cart as NonNullable<PageInput['cart']>), qtyColumn: e.target.value } })}
              style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
          </div>
        </div>
        {/* Booking lines (Add to cart → Book dates) also carry their range. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginTop: 6 }}>
          {([
            ['startColumn', 'Start date column', 'CHECK_IN'],
            ['endColumn', 'End date column', 'CHECK_OUT'],
            ['unitsColumn', 'Nights / days column', 'NIGHTS'],
          ] as const).map(([key, label, ph]) => (
            <div key={key}>
              <label style={MINI_LABEL_STYLE}>{label}</label>
              <input value={inp.cart?.[key] ?? ''} placeholder={ph}
                onChange={(e) => onPatch({ cart: { ...(inp.cart as NonNullable<PageInput['cart']>), [key]: e.target.value || undefined } })}
                style={{ ...INPUT_STYLE, fontFamily: 'monospace' }} />
            </div>
          ))}
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
          Only for date bookings — leave empty for ordinary products. The line's
          own logic prices rate × nights and checks availability on save.
        </div>
      </SourceToggle>

      {/* ── Section: Computed expression ─────────────── */}
      <SourceToggle
        label="Computed default"
        description="Re-evaluates whenever a referenced variable changes. Serves as the value until the user types or an API responds."
        enabled={hasExpr}
        onToggle={(on) => onPatch({ expression: on ? (inp.expression ?? '') : undefined })}
      >
        <input
          value={inp.expression ?? ''}
          onChange={(e) => onPatch({ expression: e.target.value })}
          placeholder={'{{firstName}} + " " + {{lastName}}'}
          style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Strings, numbers, comparisons, <code>&amp;&amp;</code>, <code>||</code>, <code>+</code>, <code>-</code>, <code>*</code>, <code>/</code> are allowed.
        </div>
      </SourceToggle>

      <div style={{ height: 1, background: 'var(--color-border)' }} />

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={inp.display ?? false}
          onChange={(e) => onPatch({ display: e.target.checked, isInput: e.target.checked ? false : (inp.isInput ?? true) })}
        />
        Hide from forms (treat as read-only display variable)
      </label>

      <div>
        <label style={FIELD_LABEL_STYLE}>Help text (optional)</label>
        <input
          value={inp.helpText ?? ''}
          onChange={(e) => onPatch({ helpText: e.target.value || undefined })}
          placeholder="A muted hint shown under the input"
          style={INPUT_STYLE}
        />
      </div>
    </div>
  )
}

// Editor for a JSON variable's literal default. Stored as a stringified
// JSON blob on disk so the existing PageInput.default (string) shape can
// hold it; the runtime parses it back into an object/array. Shows a
// parse-error message inline and lets the author keep typing without
// destroying their input.
function JsonDefaultEditor({
  value, onChange,
}: {
  value: string | undefined
  onChange: (next: string | undefined) => void
}) {
  const [text, setText] = useState<string>(() => prettyJsonOrText(value))
  const [error, setError] = useState<string | null>(() => parseError(value))

  // Keep local state in sync if the model is patched from outside
  // (e.g. AI generation or undo) — only when the canonical text differs
  // from what's currently in the textarea.
  useEffect(() => {
    const incoming = prettyJsonOrText(value)
    if (incoming !== text) {
      setText(incoming)
      setError(parseError(value))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <div>
      <textarea
        value={text}
        rows={8}
        spellCheck={false}
        onChange={(e) => {
          const next = e.target.value
          setText(next)
          if (next.trim() === '') {
            setError(null)
            onChange(undefined)
            return
          }
          try {
            JSON.parse(next)
            setError(null)
            onChange(next)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid JSON')
            // Don't propagate invalid JSON — keep the last valid value on disk.
          }
        }}
        placeholder={'{\n  "items": []\n}'}
        style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace', minHeight: 140 }}
      />
      {error && (
        <div style={{ fontSize: 13, color: 'var(--color-error, #b91c1c)', marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  )
}

function prettyJsonOrText(value: string | undefined): string {
  if (!value) return ''
  try {
    return JSON.stringify(JSON.parse(value), null, 2)
  } catch {
    return value
  }
}
function parseError(value: string | undefined): string | null {
  if (!value || value.trim() === '') return null
  try {
    JSON.parse(value)
    return null
  } catch (err) {
    return err instanceof Error ? err.message : 'Invalid JSON'
  }
}

function SourceToggle({
  label, description, enabled, onToggle, children,
}: {
  label: string
  description: string
  enabled: boolean
  onToggle: (on: boolean) => void
  children: React.ReactNode
}) {
  return (
    <div style={{
      borderLeft: '3px solid ' + (enabled ? 'var(--color-primary)' : 'var(--color-border)'),
      paddingLeft: 10, display: 'grid', gap: 8,
    }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          style={{ marginTop: 2 }}
        />
        <span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>{description}</div>
        </span>
      </label>
      {enabled && <div style={{ display: 'grid', gap: 8, paddingLeft: 22 }}>{children}</div>}
    </div>
  )
}

function ApiSourceEditor({
  source, onChange, allVars,
}: {
  source: NonNullable<PageInput['api']>
  onChange: (next: PageInput['api']) => void
  allVars?: PageInput[]
}) {
  const set = (patch: Partial<NonNullable<PageInput['api']>>) => onChange({ ...source, ...patch })
  const [headersText, setHeadersText] = useState(
    source.headers ? Object.entries(source.headers).map(([k, v]) => `${k}: ${v}`).join('\n') : '',
  )
  const [bodyText, setBodyText] = useState(
    source.body == null ? '' : typeof source.body === 'string' ? source.body : JSON.stringify(source.body, null, 2),
  )
  return (
    <div style={{
      display: 'grid', gap: 8, padding: 10,
      border: '1px solid var(--color-border)', borderRadius: 6,
      background: 'var(--color-bg-secondary)',
    }}>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
        REST endpoint
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Route via IOFlow API (optional)</label>
        <input
          value={source.ioflow_api ?? ''}
          onChange={(e) => set({ ioflow_api: e.target.value.trim() || undefined })}
          placeholder="api_name from Advanced → Events & APIs"
          style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        />
        {source.ioflow_api && (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            Sent through IOFlow — the flow subscribed to <code>api.{source.ioflow_api}</code> answers it. The URL and headers below are ignored.
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 6 }}>
        <select value={source.method} onChange={(e) => set({ method: e.target.value as any })} style={INPUT_STYLE}>
          {(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const).map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          value={source.url}
          onChange={(e) => set({ url: e.target.value })}
          placeholder="/api/orders/{{order_id}} or https://…"
          style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        />
      </div>

      <div>
        <label style={FIELD_LABEL_STYLE}>Pick path (optional)</label>
        <input
          value={source.pickPath ?? ''}
          onChange={(e) => set({ pickPath: e.target.value || undefined })}
          placeholder="data.user.email"
          style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        />
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
          Dotted path into the JSON response. Leave blank to store the whole body.
        </div>
      </div>

      <details>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', userSelect: 'none' }}>
          Headers &amp; body
        </summary>
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <div>
            <label style={FIELD_LABEL_STYLE}>Headers (one per line: <code>Key: value</code>)</label>
            <textarea
              rows={3}
              value={headersText}
              onChange={(e) => setHeadersText(e.target.value)}
              onBlur={() => {
                const headers: Record<string, string> = {}
                for (const line of headersText.split('\n')) {
                  const i = line.indexOf(':')
                  if (i > 0) headers[line.slice(0, i).trim()] = line.slice(i + 1).trim()
                }
                set({ headers: Object.keys(headers).length ? headers : undefined })
              }}
              style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace', resize: 'vertical' }}
              placeholder="Authorization: Bearer {{token}}"
            />
          </div>
          {(source.method === 'POST' || source.method === 'PUT' || source.method === 'PATCH') && (
            <div>
              <label style={FIELD_LABEL_STYLE}>JSON body</label>
              <textarea
                rows={4}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                onBlur={() => {
                  if (!bodyText.trim()) { set({ body: undefined }); return }
                  try { set({ body: JSON.parse(bodyText) }) }
                  catch { set({ body: bodyText }) }   // fall back to raw string
                }}
                style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, SFMono-Regular, monospace', resize: 'vertical' }}
                placeholder={'{ "id": "{{user_id}}" }'}
              />
            </div>
          )}
        </div>
      </details>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
        <input
          type="checkbox"
          checked={source.autoload ?? false}
          onChange={(e) => set({ autoload: e.target.checked })}
        />
        Auto-fetch on page load
      </label>

      {/* Refire when any of these variables changes */}
      <div>
        <label style={FIELD_LABEL_STYLE}>Refire when these variables change</label>
        {allVars && allVars.length > 0 ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {allVars.map(v => {
              const on = (source.triggerOn ?? []).includes(v.name)
              return (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => {
                    const cur = new Set(source.triggerOn ?? [])
                    on ? cur.delete(v.name) : cur.add(v.name)
                    set({ triggerOn: cur.size ? Array.from(cur) : undefined })
                  }}
                  style={{
                    padding: '3px 10px', fontSize: 13, fontWeight: 500,
                    borderRadius: 999,
                    border: '1px solid ' + (on ? 'var(--color-primary)' : 'var(--color-border)'),
                    background: on ? 'var(--color-primary)' : 'transparent',
                    color: on ? '#fff' : 'var(--color-text)',
                    cursor: 'pointer', fontFamily: 'monospace',
                  }}
                >
                  {v.name}
                </button>
              )
            })}
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            Add other variables on this page to drive automatic refires.
          </div>
        )}
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          When any of the selected variables changes, this API is re-fetched and overwrites this variable.
        </div>
      </div>
    </div>
  )
}

// ─── Single input editor ─────────────────────────────────

function SingleInputEditor({
  page,
  index,
  onPageChange,
}: {
  page: PageModel
  index: number
  onPageChange: (p: PageModel) => void
}) {
  const inp = page.inputs[index]
  if (!inp) return <EmptyState text="Input not found" />

  const update = (patch: Partial<PageInput>) => {
    const next = [...page.inputs]
    next[index] = { ...inp, ...patch }
    onPageChange({ ...page, inputs: next })
  }

  return (
    <div style={SECTION_STYLE}>
      <h3 style={sectionHeader}>Input: {inp.label || inp.name}</h3>
      <div>
        <label style={FIELD_LABEL_STYLE}>Name (identifier)</label>
        <input
          value={inp.name}
          onChange={(e) => update({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_') })}
          style={{ ...INPUT_STYLE, fontFamily: 'monospace' }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Label</label>
        <input value={inp.label} onChange={(e) => update({ label: e.target.value })} style={INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Type</label>
        <select value={inp.type} onChange={(e) => update({ type: e.target.value as PageInputType })} style={INPUT_STYLE}>
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
          <option value="checkbox">Checkbox</option>
          <option value="select">Select</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Default Value</label>
        <input value={inp.default || ''} onChange={(e) => update({ default: e.target.value })} style={INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Placeholder</label>
        <input value={inp.placeholder || ''} onChange={(e) => update({ placeholder: e.target.value })} style={INPUT_STYLE} />
      </div>
      {inp.type === 'select' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Options (one per line: value|label)</label>
          <textarea
            rows={4}
            value={(inp.options || []).map((o) => `${o.value}|${o.label}`).join('\n')}
            onChange={(e) => {
              const opts = e.target.value
                .split('\n')
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line) => {
                  const [v, l] = line.split('|').map((x) => x.trim())
                  return { value: v || '', label: l || v || '' }
                })
              update({ options: opts })
            }}
            style={{ ...INPUT_STYLE, fontFamily: 'monospace', resize: 'vertical' }}
          />
        </div>
      )}

      <ValidationsEditor
        rules={inp.validations || []}
        onChange={(rules) => update({ validations: rules.length ? rules : undefined })}
      />
    </div>
  )
}

// Dedicated select-options editor. Uses LOCAL state so we don't filter
// empty lines on every keystroke (which would eat newlines as the user
// types). Only on blur, or when the user clicks "Apply", do we normalise
// and write back to the page model.
function SelectOptionsEditor({
  options, onChange,
}: {
  options?: PageInput['options']
  onChange: (next: PageInput['options']) => void
}) {
  const initial = useMemo(
    () => (options ?? []).map(o => `${o.value}|${o.label}`).join('\n'),
    [options],
  )
  const [text, setText] = useState(initial)

  // Reseed local state if the parent's options change from outside
  // (e.g. user picks another input card). Only when the canonical
  // serialisation differs from the local buffer, to avoid clobbering
  // the user's in-progress newlines.
  useEffect(() => {
    const canonical = (options ?? []).map(o => `${o.value}|${o.label}`).join('\n')
    if (parseOptions(text).text !== canonical) setText(canonical)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial])

  const commit = (raw: string) => {
    const { opts } = parseOptions(raw)
    onChange(opts.length ? opts : undefined)
  }

  return (
    <div>
      <label style={FIELD_LABEL_STYLE}>Options (one per line: <code>value|label</code>)</label>
      <textarea
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        placeholder={'red|Red\ngreen|Green\nblue|Blue'}
        style={{ ...INPUT_STYLE, fontFamily: 'monospace', resize: 'vertical', whiteSpace: 'pre' }}
      />
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
        Press <kbd>Enter</kbd> to start a new option. Click outside to apply.
      </div>
    </div>
  )
}

function parseOptions(raw: string): { opts: NonNullable<PageInput['options']>; text: string } {
  const opts = raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [v, l] = line.split('|').map((x) => x.trim())
      return { value: v || '', label: l || v || '' }
    })
  const text = opts.map(o => `${o.value}|${o.label}`).join('\n')
  return { opts, text }
}


// ─── Block-id editor ──────────────────────────────
// The block id is the stable handle the runtime uses to reference a block
// (animations, deep-links, css selectors, future API hooks). New blocks
// auto-generate an id like `${type}_${seq}` so it's predictable and easy
// to spot in the page JSON, but the user can rename it here at any time —
// we just refuse blanks, whitespace, or ids that already belong to
// another block. The new id is committed on blur or Enter; Escape reverts
// to the current id.
function BlockIdEditor({
  block,
  page,
  onChange,
}: {
  block: Block
  page: PageModel
  onChange: (b: Block) => void
}) {
  const [draft, setDraft] = useState(block.id)
  // Keep the draft in sync if the block (or its id) changes underneath us
  // (e.g. selection switches, undo/redo).
  useEffect(() => { setDraft(block.id) }, [block.id])

  const trimmed = draft.trim()
  const isValid = trimmed.length > 0 && /^[A-Za-z0-9_\-.]+$/.test(trimmed) && isBlockIdAvailable(page, trimmed, block.id)
  const isUnchanged = trimmed === block.id

  const commit = () => {
    if (isUnchanged) { setDraft(block.id); return }
    if (!isValid) { setDraft(block.id); return }
    onChange({ ...block, id: trimmed })
  }

  return (
    <div>
      <label style={FIELD_LABEL_STYLE}>Block ID</label>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur() }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(block.id); (e.currentTarget as HTMLInputElement).blur() }
        }}
        spellCheck={false}
        autoComplete="off"
        style={{
          ...INPUT_STYLE,
          fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
          borderColor: !isUnchanged && !isValid ? 'var(--pg-error, #dc2626)' : 'var(--color-border)',
        }}
      />
      <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        {!isUnchanged && !isValid
          ? (trimmed.length === 0
            ? 'ID can’t be empty.'
            : !/^[A-Za-z0-9_\-.]+$/.test(trimmed)
              ? 'Use letters, numbers, dash, dot, or underscore.'
              : 'Another block already uses this ID.')
          : 'Auto-generated as type_seq. Edit to give the block a meaningful name.'}
      </div>
    </div>
  )
}

// ─── Collection bindings ──────────────────────────
// For blocks that hold a list of items as a top-level setting
// (card_grid.cards, feature_grid.items, gallery.items, table.rows,
// etc.), surface a BindingPicker so the author can wire the whole
// collection to a page variable — typically a `type:"json"` variable
// populated by an API. At runtime BindingResolverService overwrites
// the static array with the resolved value, and the block re-renders
// automatically whenever the variable changes.

const BLOCK_COLLECTION_PATHS: Partial<Record<Block['type'], { path: string; label: string }[]>> = {
  card_grid:    [{ path: 'cards', label: 'Cards' }],
  feature_grid: [{ path: 'items', label: 'Features' }],
  gallery:      [{ path: 'items', label: 'Images' }],
  table:        [
    { path: 'rows',    label: 'Rows' },
    { path: 'columns', label: 'Columns' },
  ],
  logo_cloud:   [{ path: 'items', label: 'Logos' }],
  pricing:      [{ path: 'tiers', label: 'Pricing tiers' }],
  faq:          [{ path: 'items', label: 'Q&A items' }],
  tabs:         [{ path: 'items', label: 'Tabs' }],
  accordion:    [{ path: 'items', label: 'Sections' }],
  stats:        [{ path: 'items', label: 'Stats' }],
  timeline:     [{ path: 'items', label: 'Events' }],
  bullet_list:  [{ path: 'items', label: 'List items' }],
  numbered_list:[{ path: 'items', label: 'List items' }],
  todo:         [{ path: 'items', label: 'To-do items' }],
  hero:         [{ path: 'slides', label: 'Carousel slides' }],
  testimonial:  [{ path: 'items', label: 'Quotes' }],
  button_group: [{ path: 'buttons', label: 'Buttons' }],
  bento_grid:   [{ path: 'cells', label: 'Cells' }],
  marquee:      [{ path: 'items', label: 'Items' }],
  counter:      [{ path: 'items', label: 'Counters' }],
  carousel:     [{ path: 'slides', label: 'Slides' }],
  team:         [{ path: 'members', label: 'Members' }],
  steps:        [{ path: 'steps', label: 'Steps' }],
  comparison_table: [{ path: 'rows', label: 'Rows' }, { path: 'columns', label: 'Columns' }],
  social_links: [{ path: 'links', label: 'Links' }],
  breadcrumb:   [{ path: 'items', label: 'Crumbs' }],
  nav_menu:     [{ path: 'items', label: 'Menu items' }],
  auth_menu:    [{ path: 'links', label: 'Menu links' }],
  chatbot:      [{ path: 'suggestedQuestions', label: 'Suggested questions' }],
  form:         [{ path: 'fields', label: 'Fields' }],
  contact_form: [{ path: 'fields', label: 'Fields' }],
  filter_bar:   [{ path: 'facets', label: 'Facets' }, { path: 'sort', label: 'Sort options' }],
}

function CollectionBindingsSection({
  block, page, project, transactions, onChange,
}: {
  block: Block
  page: PageModel
  project: string
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const targets = BLOCK_COLLECTION_PATHS[block.type]
  if (!targets || targets.length === 0) return null

  const bindings = (block as Block & { bindings?: Record<string, BindingExpr> }).bindings
  // Show only if at least one collection-bindable target exists. We
  // surface the section unconditionally so authors discover the feature
  // without having to hunt; the Bind button is the inline lightning bolt.
  return (
    <div style={{
      padding: 10, borderRadius: 6,
      background: 'var(--color-bg-secondary)', border: '1px dashed var(--color-border)',
      display: 'grid', gap: 8,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Bind to variable
      </div>
      {targets.map(({ path, label }) => {
        const current = bindings?.[path]
        return (
          <div key={path} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {current ? describeBinding(current) : 'Static (edit items below)'}
              </span>
            </div>
            <BindingPicker
              value={current}
              onChange={(expr) => {
                const cur = bindings ? { ...bindings } : {}
                if (expr === undefined) delete cur[path]
                else cur[path] = expr
                const nextBindings = Object.keys(cur).length ? cur : undefined
                onChange({ ...block, bindings: nextBindings } as Block)
              }}
              inputs={page.inputs}
              // A collection can come from a list data source as readily as
              // from a json page variable — offer both.
              dataSources={page.dataSources}
              resourceCandidates={(transactions || []).map((t) => ({ obj_type: 'T', obj_name: t.obj_name, label: t.title || t.obj_name }))}
              project={project}
            />
          </div>
        )
      })}
    </div>
  )
}

function describeBinding(b: BindingExpr): string {
  switch (b.source) {
    case 'page_input':     return `from variable: ${b.name || '(none)'}`
    case 'metafield':      return `from metafield: ${b.name || '(none)'}`
    case 'resource_field': return `from ${b.resource?.obj_name ?? '?'}.${b.path ?? '?'}`
    case 'data_field':     return `from data: ${b.dataSource ?? '?'}.${b.path ?? '?'}`
    case 'item_field':     return `from row${b.scope ? `(${b.scope})` : ''}.${b.path ?? '?'}`
    case 'expression':     return `expression: ${b.expression?.slice(0, 40) ?? ''}`
    case 'static':         return 'static value'
    default:               return 'binding'
  }
}

// ─── Scalar field bindings ────────────────────────
// For ordinary scalar settings (hero title, paragraph text, card title,
// map address, testimonial quote, ...), surface a BindingPicker per field
// so authors can wire it to a data source (record → data_field, repeater
// row → item_field) or a page variable without editing JSON. Writes
// block.bindings[path]; the runtime's applyBindings overlays the resolved
// value, falling back to the static value when the source is empty.

// The click-target scalars a bound button carries. `url` on the spec itself
// is ignored once `on_click` exists (resolveClick prefers it), so a bound link
// goes through `on_click.url`; the add_to_cart payload is what a catalogue
// card's "Add to cart" binds per row.
const btnPaths = (prefix: string, label: string) => [
  { path: `${prefix}.label`, label: `${label} label` },
  { path: `${prefix}.on_click.url`, label: `${label} link URL` },
]
const cartPaths = (prefix: string, label: string) => [
  { path: `${prefix}.on_click.sku`, label: `${label} cart SKU` },
  { path: `${prefix}.on_click.name`, label: `${label} cart item name` },
  { path: `${prefix}.on_click.price`, label: `${label} cart price` },
  { path: `${prefix}.on_click.image`, label: `${label} cart image` },
  { path: `${prefix}.on_click.href`, label: `${label} cart item link` },
]
const text = (...pairs: [string, string][]) => pairs.map(([path, label]) => ({ path, label }))

const BLOCK_FIELD_PATHS: Partial<Record<Block['type'], { path: string; label: string }[]>> = {
  hero:         [...text(['eyebrow', 'Eyebrow'], ['title', 'Title'], ['subtitle', 'Subtitle'], ['imageUrl', 'Image URL'], ['background.imageUrl', 'Background image URL']),
                 ...btnPaths('primaryCta', 'Primary CTA'), ...btnPaths('secondaryCta', 'Secondary CTA')],
  cta_banner:   [...text(['eyebrow', 'Eyebrow'], ['title', 'Title'], ['subtitle', 'Subtitle'], ['background.imageUrl', 'Background image URL']),
                 ...btnPaths('primaryCta', 'Primary CTA'), ...btnPaths('secondaryCta', 'Secondary CTA')],
  heading:      text(['eyebrow', 'Eyebrow'], ['text', 'Text']),
  paragraph:    text(['text', 'Text']),
  quote:        text(['text', 'Text']),
  code:         text(['text', 'Code'], ['language', 'Language']),
  callout:      text(['title', 'Title'], ['body', 'Body'], ['icon', 'Icon']),
  container:    text(['text', 'Text'], ['background.imageUrl', 'Background image URL'], ['background.color', 'Background colour']),
  // Every scalar of CardSpec a catalogue binds — a card repeated over a
  // product list binds price, eyebrow and status as often as the title.
  card:         [
    ...text(['card.eyebrow', 'Card eyebrow'], ['card.title', 'Card title'], ['card.subtitle', 'Card subtitle'],
      ['card.body', 'Card body'], ['card.imageUrl', 'Card image URL'], ['card.hoverImageUrl', 'Card hover image URL'],
      ['card.imageAlt', 'Card image alt text'],
      ['card.badge', 'Card badge'], ['card.price', 'Card price'], ['card.compareAtPrice', 'Card was price (MRP)'], ['card.priceNote', 'Card price note'],
      ['card.priceCurrency', 'Card price currency'], ['card.status.label', 'Card status label'],
      ['card.status.tone', 'Card status tone'], ['card.wishlistSku', 'Card wishlist item code']),
    ...btnPaths('card.cta', 'Card CTA'),
    ...btnPaths('card.secondaryCta', 'Card second action'), ...cartPaths('card.secondaryCta', 'Card second action'),
  ],
  testimonial:  text(['quote', 'Quote'], ['authorName', 'Author name'], ['authorRole', 'Author role'], ['authorAvatarUrl', 'Author avatar URL'], ['companyLogoUrl', 'Company logo URL'], ['rating', 'Rating']),
  map:          text(['address', 'Address'], ['latitude', 'Latitude'], ['longitude', 'Longitude'], ['zoom', 'Zoom'], ['title', 'Title'], ['caption', 'Caption']),
  image:        text(['url', 'Image URL'], ['alt', 'Alt text'], ['caption', 'Caption']),
  video:        text(['url', 'Video URL'], ['caption', 'Caption']),
  button:       [...text(['button.label', 'Button label'], ['button.icon', 'Button icon']),
                 { path: 'button.on_click.url', label: 'Button link URL' }, ...cartPaths('button', 'Button')],
  pricing:      text(['heading', 'Heading'], ['subheading', 'Subheading']),
  feature_grid: text(['eyebrow', 'Eyebrow'], ['heading', 'Heading'], ['subheading', 'Subheading']),
  stats:        text(['heading', 'Heading']),
  table:        text(['heading', 'Heading'], ['subheading', 'Subheading'], ['caption', 'Caption']),
  logo_cloud:   text(['title', 'Title']),
  countdown:    text(['target', 'Target date/time'], ['heading', 'Heading'], ['expiredText', 'Text when finished'],
                  ['labels.days', 'Days label'], ['labels.hours', 'Hours label'], ['labels.minutes', 'Minutes label'], ['labels.seconds', 'Seconds label']),
  faq:          text(['heading', 'Heading']),
  timeline:     text(['eyebrow', 'Eyebrow'], ['heading', 'Heading'], ['subheading', 'Subheading']),
  bento_grid:   text(['eyebrow', 'Eyebrow'], ['heading', 'Heading'], ['subheading', 'Subheading']),
  counter:      text(['heading', 'Heading']),
  before_after: text(['beforeUrl', 'Before image URL'], ['afterUrl', 'After image URL'], ['beforeLabel', 'Before label'], ['afterLabel', 'After label'], ['caption', 'Caption']),
  team:         text(['eyebrow', 'Eyebrow'], ['heading', 'Heading'], ['subheading', 'Subheading']),
  steps:        text(['eyebrow', 'Eyebrow'], ['heading', 'Heading'], ['subheading', 'Subheading']),
  comparison_table: text(['heading', 'Heading'], ['subheading', 'Subheading']),
  social_links: text(['shareTitle', 'Share title']),
  lottie:       text(['url', 'Animation URL'], ['posterUrl', 'Poster URL'], ['caption', 'Caption']),
  cookie_consent: text(['message', 'Message'], ['acceptLabel', 'Accept label'], ['declineLabel', 'Decline label'], ['policyHref', 'Policy link'], ['policyLabel', 'Policy link label']),
  newsletter:   text(['heading', 'Heading'], ['subheading', 'Subheading'], ['placeholder', 'Email placeholder'], ['buttonLabel', 'Button label'], ['success.message', 'Success message']),
  contact_form: text(['heading', 'Heading'], ['subheading', 'Subheading'], ['buttonLabel', 'Button label'], ['success.title', 'Success title'], ['success.message', 'Success message']),
  form:         text(['heading', 'Heading'], ['subheading', 'Subheading'], ['submitLabel', 'Submit label'], ['success.title', 'Success title'], ['success.message', 'Success message'], ['success.redirectUrl', 'Redirect URL'], ['notifyEmail', 'Notify email']),
  rich_content: text(['source', 'Content']),
  variable:     text(['label', 'Label'], ['prefix', 'Prefix'], ['suffix', 'Suffix'], ['emptyText', 'Empty text']),
  collection_search: text(['placeholder', 'Placeholder'], ['emptyText', 'Empty text']),
  comments:     text(['recordSlug', 'Record slug'], ['heading', 'Heading'], ['formHeading', 'Form heading'], ['submitLabel', 'Submit label']),
  chatbot:      text(['title', 'Title'], ['greeting', 'Greeting'], ['systemPrompt', 'System prompt']),
  auth_form:    text(['heading', 'Heading'], ['subheading', 'Subheading'], ['submitLabel', 'Submit label']),
  auth_menu:    text(['loginLabel', 'Log in label'], ['signupLabel', 'Sign up label']),
  filter_bar:   text(['resultLabel', 'Result label'], ['search.label', 'Search label'], ['search.placeholder', 'Search placeholder']),
  file_upload:  text(['label', 'Label']),
  notepad:      text(['html', 'Content']),
  custom_html:  text(['html', 'HTML']),
  breadcrumb:   text(['separator', 'Separator']),
  cart_badge:   text(['label', 'Label'], ['href', 'Link']),
  cart_lines:   text(['emptyText', 'Empty cart message'], ['continueLabel', 'Continue label'], ['continueHref', 'Continue link']),
  // A wishlist entry is a product reference, so a heart repeated over a
  // product list binds its whole payload per row, like an add-to-cart.
  wishlist_button: text(['sku', 'Item code'], ['name', 'Name'], ['price', 'Price'], ['image', 'Image URL'], ['href', 'Links back to'], ['meta', 'Second line'], ['addLabel', 'Label (not saved)'], ['removeLabel', 'Label (saved)']),
  wishlist_badge:  text(['label', 'Label'], ['href', 'Link']),
  wishlist_lines:  text(['emptyText', 'Empty message'], ['continueLabel', 'Continue label'], ['continueHref', 'Continue link'], ['moveLabel', 'Move label'], ['removeLabel', 'Remove label']),
  cart_summary: text(['heading', 'Heading'], ['note', 'Note'], ['checkoutLabel', 'Checkout label'], ['checkoutHref', 'Checkout link'],
                  ['taxPercent', 'Tax %'], ['shippingFlat', 'Flat shipping'], ['shippingFreeOver', 'Free shipping over']),
  // Presentation text only. The payment column bindings (objName,
  // amountColumn, ownerColumn, writeback.*) are deliberately NOT bindable —
  // each names a column and must never carry a value from the page.
  payment_checkout: text(['heading', 'Heading'], ['description', 'Description'], ['buttonLabel', 'Button label'], ['successTitle', 'Success title'], ['successBody', 'Success message'], ['codLabel', 'Cash-on-delivery label'], ['onSuccessNavigateTo', 'Go to after paying']),
  payment_settings: text(['heading', 'Heading'], ['description', 'Description']),
  order_submit: text(['buttonLabel', 'Button label'], ['successTitle', 'Success title'], ['successBody', 'Success message'], ['onSuccessNavigateTo', 'Go to after submitting']),
  order_summary: text(['emptyStateTitle', 'Empty title'], ['emptyStateBody', 'Empty message']),
  product_preview: text(['emptyStateTitle', 'Empty title'], ['emptyStateBody', 'Empty message']),
  entity_picker: text(['searchPlaceholder', 'Search placeholder']),
  new_customer_form: text(['submitLabel', 'Submit label']),
  product_selection: text(['catalogueTitle', 'Catalogue title'], ['catalogueButtonLabel', 'Catalogue button label']),
}

// Static paths plus the per-item ones a list block's items generate: a
// button group binds `buttons.N.…` per button (a catalogue's add-to-cart
// buttons bind their SKU this way).
function fieldPathsFor(block: Block): { path: string; label: string }[] {
  const base = BLOCK_FIELD_PATHS[block.type] ?? []
  if (block.type === 'button_group') {
    const per = (block.buttons || []).flatMap((b, i) => {
      const name = `Button ${i + 1}${b?.label ? ` (${b.label})` : ''}`
      return [...btnPaths(`buttons.${i}`, name), ...cartPaths(`buttons.${i}`, name)]
    })
    return [...base, ...per]
  }
  return base
}

function FieldBindingsSection({
  block, page, project, transactions, onChange,
}: {
  block: Block
  page: PageModel
  project: string
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const bindings = (block as Block & { bindings?: Record<string, BindingExpr> }).bindings
  // The runtime overlays a binding at ANY dotted path, so a model (agent-
  // authored or hand-edited) can bind a path this list doesn't name. Show
  // those too — a binding the designer can't see is one nobody can edit or
  // remove. Collection paths have their own section above.
  const known = fieldPathsFor(block)
  const collectionPaths = new Set((BLOCK_COLLECTION_PATHS[block.type] ?? []).map((t) => t.path))
  const knownPaths = new Set(known.map((t) => t.path))
  const extras = Object.keys(bindings ?? {})
    .filter((p) => !knownPaths.has(p) && !collectionPaths.has(p))
    .map((p) => ({ path: p, label: p }))
  const all = [...known, ...extras]
  const [showAll, setShowAll] = React.useState(false)
  if (all.length === 0) return null
  // Bound fields always show; the rest of a long list folds away so a card
  // (fifteen bindable fields) doesn't bury the rest of the panel.
  const FOLD_AT = 6
  const fold = all.length > FOLD_AT && !showAll
  const targets = fold ? all.filter((t) => bindings?.[t.path]) : all
  const hidden = all.length - targets.length
  const resourceCandidates = (transactions || []).map((t) => ({
    obj_type: 'T', obj_name: t.obj_name, label: t.title || t.obj_name,
  }))

  return (
    <div style={{
      padding: 10, borderRadius: 6,
      background: 'var(--color-bg-secondary)', border: '1px dashed var(--color-border)',
      display: 'grid', gap: 8,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Bind fields to data
      </div>
      {targets.map(({ path, label }) => {
        const current = bindings?.[path]
        return (
          <div key={path} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {current ? describeBinding(current) : 'Static'}
              </span>
            </div>
            <BindingPicker
              value={current}
              onChange={(expr) => {
                const cur = bindings ? { ...bindings } : {}
                if (expr === undefined) delete cur[path]
                else cur[path] = expr
                const nextBindings = Object.keys(cur).length ? cur : undefined
                onChange({ ...block, bindings: nextBindings } as Block)
              }}
              inputs={page.inputs}
              dataSources={page.dataSources}
              resourceCandidates={resourceCandidates}
              project={project}
            />
          </div>
        )
      })}
      {(hidden > 0 || (showAll && all.length > FOLD_AT)) && (
        <button type="button" className="btn btn-ghost btn-sm" style={{ justifySelf: 'start', padding: '2px 6px' }}
          onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show bound fields only' : `Bind another field (${hidden} more)…`}
        </button>
      )}
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
        Pick <b>Data source field</b> for a record source, or <b>Row field</b> when this block repeats over a list. Empty values fall back to the static content.
      </p>
    </div>
  )
}

// ─── Transaction data binding: page-level sources + block repeat ─────

function newDataSourceId(existing: PageDataSource[]): string {
  let n = existing.length + 1
  const ids = new Set(existing.map((s) => s.id))
  while (ids.has(`ds_${n}`)) n += 1
  return `ds_${n}`
}

// Rename a data source everywhere the page points at it: block repeats and
// `data_field` bindings, at any depth. A rename used to orphan every block
// that repeated over the old id.
function renameDataSourceRefs(page: PageModel, from: string, to: string): PageModel {
  const fixBlock = (b: Block): Block => {
    let next = b as Block & { repeat?: { source?: string }; bindings?: Record<string, BindingExpr> }
    if (next.repeat?.source === from) next = { ...next, repeat: { ...next.repeat, source: to } } as typeof next
    if (next.bindings && Object.values(next.bindings).some((x) => x?.source === 'data_field' && x.dataSource === from)) {
      next = {
        ...next,
        bindings: Object.fromEntries(Object.entries(next.bindings).map(([k, x]) => [
          k, x?.source === 'data_field' && x.dataSource === from ? { ...x, dataSource: to } : x,
        ])),
      }
    }
    // Walk every nested block list the model carries (children, tab/accordion
    // item blocks, column blocks) generically.
    const walked: Record<string, unknown> = { ...(next as unknown as Record<string, unknown>) }
    for (const [k, v] of Object.entries(walked)) {
      if (k === 'bindings' || k === 'repeat') continue
      walked[k] = walkValue(v)
    }
    return walked as unknown as Block
  }
  const walkValue = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walkValue)
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>
      if (typeof o.type === 'string' && typeof o.id === 'string') return fixBlock(o as unknown as Block)
      return Object.fromEntries(Object.entries(o).map(([k, x]) => [k, walkValue(x)]))
    }
    return v
  }
  return {
    ...page,
    blocks: page.blocks?.map(fixBlock) ?? page.blocks,
    sections: page.sections?.map((sec) => ({ ...sec, blocks: (sec.blocks || []).map(fixBlock) })) ?? page.sections,
  }
}

function DataSourcesEditor({
  page, transactions, onPageChange,
}: {
  page: PageModel
  transactions: { obj_name: string; title: string }[]
  onPageChange: (p: PageModel) => void
}) {
  const sources = page.dataSources || []
  const setSources = (next: PageDataSource[]) => onPageChange({ ...page, dataSources: next })
  const patch = (i: number, p: Partial<PageDataSource>) =>
    setSources(sources.map((s, idx) => (idx === i ? { ...s, ...p } : s)))
  const rename = (i: number, raw: string) => {
    const from = sources[i].id
    const to = raw.trim()
    if (!to || to === from || sources.some((s, idx) => idx !== i && s.id === to)) return
    const renamed = renameDataSourceRefs(page, from, to)
    onPageChange({ ...renamed, dataSources: sources.map((s, idx) => (idx === i ? { ...s, id: to } : s)) })
  }

  const add = () => setSources([
    ...sources,
    {
      id: newDataSourceId(sources),
      resource: { obj_type: 'T', obj_name: transactions[0]?.obj_name || '' },
      mode: 'list',
      bindingTime: 'deployment',
      limit: 20,
    },
  ])

  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={{ ...FIELD_LABEL_STYLE, margin: 0 }}>Data sources</label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={add}>
          <Plus size={14} /> Add
        </button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
        Bind blocks to a transaction object's data. Default <b>deployment</b> (prefilled
        at deploy/preview); <b>runtime</b> reads live (cached).
      </p>
      {sources.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>No data sources yet.</div>
      )}
      {sources.map((s, i) => {
        const known = transactions.some((t) => t.obj_name === s.resource.obj_name)
        const sort = s.sort || []
        const rec = s.record || { by: 'first' as const }
        return (
          // Keyed by position: keying by `id` remounted the row on every
          // keystroke of a rename, dropping focus after one character.
          <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <DraftTextField
                value={s.id}
                onCommit={(v) => rename(i, v)}
                placeholder="ds_id"
                style={{ ...INPUT_STYLE, fontFamily: 'monospace', flex: 1 }}
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSources(sources.filter((_, idx) => idx !== i))}>
                <Trash2 size={14} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <label style={MINI_LABEL_STYLE}>Object</label>
                <select
                  value={s.resource.obj_name}
                  onChange={(e) => patch(i, { resource: { ...s.resource, obj_type: s.resource.obj_type || 'T', obj_name: e.target.value } })}
                  style={INPUT_STYLE}
                >
                  <option value="">— select —</option>
                  {s.resource.obj_name && !known && (
                    <option value={s.resource.obj_name}>{s.resource.obj_name} (not in this project&apos;s list)</option>
                  )}
                  {transactions.map((t) => <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>)}
                </select>
              </div>
              <div>
                <label style={MINI_LABEL_STYLE}>Mode</label>
                <select value={s.mode} onChange={(e) => patch(i, { mode: e.target.value as 'list' | 'record' })} style={INPUT_STYLE}>
                  <option value="list">List (rows)</option>
                  <option value="record">Single record</option>
                </select>
              </div>
              <div>
                <label style={MINI_LABEL_STYLE}>Binding time</label>
                <select value={s.bindingTime} onChange={(e) => patch(i, { bindingTime: e.target.value as 'deployment' | 'runtime' })} style={INPUT_STYLE}>
                  <option value="deployment">Deployment (prefilled)</option>
                  <option value="runtime">Runtime (live)</option>
                </select>
              </div>
              {s.mode === 'list' ? (
                <div>
                  <label style={MINI_LABEL_STYLE}>Limit</label>
                  <input
                    type="number" min={1} max={200} value={s.limit ?? 20}
                    onChange={(e) => patch(i, { limit: Number(e.target.value) || 20 })}
                    style={INPUT_STYLE}
                  />
                </div>
              ) : (
                <div>
                  <label style={MINI_LABEL_STYLE}>Which record</label>
                  <select
                    value={rec.by}
                    onChange={(e) => patch(i, {
                      record: e.target.value === 'txn_id'
                        ? { by: 'txn_id', value: rec.by === 'txn_id' ? rec.value : { source: 'page_input', name: '' } }
                        : undefined,
                    })}
                    style={INPUT_STYLE}
                  >
                    <option value="first">First matching row</option>
                    <option value="txn_id">By record id</option>
                  </select>
                </div>
              )}
              <div>
                <label style={MINI_LABEL_STYLE}>Form no (multi-form)</label>
                <input
                  value={s.form_no || ''}
                  onChange={(e) => patch(i, { form_no: e.target.value.trim() || undefined })}
                  style={INPUT_STYLE}
                  placeholder="header form"
                />
              </div>
            </div>
            {s.mode === 'record' && rec.by === 'txn_id' && (
              <div style={{ marginTop: 6 }}>
                <label style={MINI_LABEL_STYLE}>Record id comes from</label>
                <DataValueRefRow
                  value={rec.value}
                  inputs={page.inputs}
                  onChange={(value) => patch(i, { record: { by: 'txn_id', value } })}
                />
              </div>
            )}
            <div style={{ marginTop: 6 }}>
              <label style={MINI_LABEL_STYLE}>Columns (comma-separated; required for public/runtime)</label>
              {/* Committed on blur — splitting on every keystroke swallowed
                  the comma, so a second column could not be typed. */}
              <DraftTextField
                value={(s.columns || []).join(', ')}
                onCommit={(v) => {
                  const cols = v.split(',').map((c) => c.trim()).filter(Boolean)
                  patch(i, { columns: cols.length ? cols : undefined })
                }}
                placeholder="name, price, image_url"
                style={INPUT_STYLE}
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!s.contentLive} onChange={(e) => patch(i, { contentLive: e.target.checked || undefined })} />
              Only published records (content collection), newest first
            </label>
            <DataSourceRefreshEditor
              source={s}
              actions={page.actions || []}
              onChange={(refresh) => patch(i, { refresh })}
            />
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={MINI_LABEL_STYLE}>Sort</label>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => patch(i, { sort: [...sort, { column: '', dir: 'asc' }] })}><Plus size={12} /></button>
              </div>
              {sort.map((so, si) => (
                <div key={si} style={{ display: 'grid', gridTemplateColumns: '1fr 0.7fr auto', gap: 4, marginTop: 4 }}>
                  <input value={so.column} placeholder="column"
                    onChange={(e) => patch(i, { sort: sort.map((x, k) => (k === si ? { ...x, column: e.target.value } : x)) })}
                    style={INPUT_STYLE} />
                  <select value={so.dir}
                    onChange={(e) => patch(i, { sort: sort.map((x, k) => (k === si ? { ...x, dir: e.target.value as 'asc' | 'desc' } : x)) })}
                    style={INPUT_STYLE}>
                    <option value="asc">asc</option>
                    <option value="desc">desc</option>
                  </select>
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => { const next = sort.filter((_, k) => k !== si); patch(i, { sort: next.length ? next : undefined }) }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <DataSourceFiltersEditor
              filters={s.filters || []}
              inputs={page.inputs}
              onChange={(filters) => patch(i, { filters })}
            />
          </div>
        )
      })}
    </div>
  )
}

// One data-source operand: static, page input, or a column of an enclosing
// repeater row (with an optional named scope for nested repeaters).
function DataValueRefRow({ value, inputs, onChange }: {
  value: DataFilterValueRef | undefined
  inputs: PageInput[]
  onChange: (v: DataFilterValueRef) => void
}) {
  const v = value || { source: 'static' as const }
  const set = (p: Partial<DataFilterValueRef>) => onChange({ ...v, ...p })
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr', gap: 4 }}>
      <select value={v.source} onChange={(e) => onChange({ source: e.target.value as DataFilterValueRef['source'] })} style={INPUT_STYLE}>
        <option value="static">static</option>
        <option value="page_input">input</option>
        <option value="item_field">row field</option>
      </select>
      {v.source === 'page_input' ? (
        <select value={v.name || ''} onChange={(e) => set({ name: e.target.value })} style={INPUT_STYLE}>
          <option value="">—</option>
          {v.name && !inputs.some((inp) => inp.name === v.name) && <option value={v.name}>{v.name} (not declared)</option>}
          {inputs.map((inp) => <option key={inp.name} value={inp.name}>{inp.name}</option>)}
        </select>
      ) : v.source === 'item_field' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr', gap: 4 }}>
          <input value={v.name || ''} onChange={(e) => set({ name: e.target.value })} style={INPUT_STYLE} placeholder="column" />
          {/* The scope is its own field: the server looks the name up as a
              literal column, so "scope.col" in the name never matched. */}
          <input value={v.scope || ''} onChange={(e) => set({ scope: e.target.value.trim() || undefined })} style={INPUT_STYLE}
            placeholder="scope" title="Alias of the enclosing repeater (blank = nearest)" />
        </div>
      ) : (
        <input value={v.value || ''} onChange={(e) => set({ value: e.target.value })} style={INPUT_STYLE} placeholder="value" />
      )}
    </div>
  )
}

function DataSourceFiltersEditor({
  filters, inputs, onChange,
}: {
  filters: DataSourceFilter[]
  inputs: PageInput[]
  onChange: (f: DataSourceFilter[]) => void
}) {
  const add = () => onChange([...filters, { column: '', op: 'eq', value: { source: 'static', value: '' } }])
  const patch = (i: number, p: Partial<DataSourceFilter>) =>
    onChange(filters.map((f, idx) => (idx === i ? { ...f, ...p } : f)))

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={MINI_LABEL_STYLE}>Filters</label>
        <button type="button" className="btn btn-ghost btn-sm" onClick={add}><Plus size={12} /></button>
      </div>
      {filters.map((f, i) => (
        <div key={i} style={{ border: '1px dashed var(--color-border)', borderRadius: 6, padding: 6, marginTop: 4, display: 'grid', gap: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr auto', gap: 4 }}>
            <input value={f.column} onChange={(e) => patch(i, { column: e.target.value })} style={INPUT_STYLE} placeholder="column" />
            <select value={f.op} onChange={(e) => patch(i, { op: e.target.value as FilterOp })} style={INPUT_STYLE}>
              {['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'contains', 'starts_with', 'in'].map((o) => <option key={o} value={o}>{o === 'in' ? 'in (any of, comma-separated)' : o}</option>)}
            </select>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(filters.filter((_, idx) => idx !== i))}><Trash2 size={12} /></button>
          </div>
          {f.op === 'between' ? (
            <>
              <DataValueRefRow value={f.from} inputs={inputs} onChange={(from) => patch(i, { from })} />
              <DataValueRefRow value={f.to} inputs={inputs} onChange={(to) => patch(i, { to })} />
            </>
          ) : (
            <DataValueRefRow value={f.value} inputs={inputs} onChange={(value) => patch(i, { value })} />
          )}
        </div>
      ))}
    </div>
  )
}

function RepeatControl({
  block, page, onChange,
}: {
  block: Block
  page: PageModel
  onChange: (b: Block) => void
}) {
  const listSources = (page.dataSources || []).filter((s) => s.mode === 'list')
  const rep = block.repeat
  // A block that already repeats keeps its control even when the page has
  // no list source (or not the one it names) — hiding it left the repeat
  // invisible and impossible to change or clear.
  if (listSources.length === 0 && !rep?.source) return null
  const sourceMissing = !!rep?.source && !listSources.some((s) => s.id === rep.source)
  return (
    <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
      <label style={FIELD_LABEL_STYLE}>Repeat over data</label>
      <select
        value={rep?.source || ''}
        onChange={(e) => {
          const src = e.target.value
          // Spread `rep` so switching the source keeps layout / row cap.
          onChange(src
            ? ({ ...block, repeat: { ...rep, source: src } } as Block)
            : ({ ...block, repeat: undefined } as Block))
        }}
        style={INPUT_STYLE}
      >
        <option value="">— none —</option>
        {sourceMissing && <option value={rep!.source}>{rep!.source} (not defined)</option>}
        {listSources.map((s) => <option key={s.id} value={s.id}>{s.id} ({s.resource.obj_name})</option>)}
      </select>
      {sourceMissing && (
        <div style={{ fontSize: 13, color: 'var(--color-error)', marginTop: 4, lineHeight: 1.5 }}>
          “{rep!.source}” is not a list data source on this page, so this block renders
          no rows. Add it under the page properties → Data sources, or pick another source.
        </div>
      )}
      {rep?.source && (
        <div style={{ marginTop: 6 }}>
          <label style={MINI_LABEL_STYLE}>Scope alias (for nested repeaters)</label>
          <input
            value={rep.as || ''}
            // Spread `rep`: rebuilding it from source+as alone silently
            // dropped the layout and the row cap every time the alias was
            // touched.
            onChange={(e) => onChange({ ...block, repeat: { ...rep, as: e.target.value.trim() || undefined } } as Block)}
            style={{ ...INPUT_STYLE, fontFamily: 'monospace' }}
            placeholder="e.g. category"
          />
          {/* Layout. Without this a repeat is always a flat stack, so a
              card bound to a product table comes out one per row down the
              page rather than as a catalogue. */}
          <label style={{ ...MINI_LABEL_STYLE, marginTop: 8 }}>Lay the rows out as</label>
          <select
            value={rep.layout?.mode || 'flow'}
            onChange={(e) => {
              const mode = e.target.value as 'flow' | 'grid' | 'board' | 'positioned'
              onChange({
                ...block,
                repeat: mode === 'grid'
                  ? { ...rep, layout: { mode, columns: rep.layout?.columns ?? 3, gap: rep.layout?.gap ?? 20 } }
                  : mode === 'board'
                    ? { ...rep, layout: { mode, groupBy: rep.layout?.groupBy, lanes: rep.layout?.lanes, gap: rep.layout?.gap ?? 16 } }
                    : mode === 'positioned'
                      ? { ...rep, layout: { mode, x: rep.layout?.x || 'POS_X', y: rep.layout?.y || 'POS_Y', canvasWidth: rep.layout?.canvasWidth ?? 1000, canvasHeight: rep.layout?.canvasHeight ?? 600 } }
                      : { ...rep, layout: undefined },
              } as Block)
            }}
            style={INPUT_STYLE}
          >
            <option value="flow">A stack, one under the next</option>
            <option value="grid">A grid</option>
            <option value="board">A board — lanes by a column</option>
            <option value="positioned">A plane — each at its own x / y</option>
          </select>
          <RepeatLayoutExtras block={block} rep={rep} page={page} onChange={onChange} />
          {rep.layout?.mode === 'grid' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <span style={{ flex: 1 }}>
                <label style={MINI_LABEL_STYLE}>Columns</label>
                <select
                  value={String(rep.layout.columns ?? 3)}
                  onChange={(e) => onChange({ ...block, repeat: { ...rep, layout: { ...rep.layout, columns: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 } } } as Block)}
                  style={INPUT_STYLE}
                >
                  {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </span>
              <span style={{ flex: 1 }}>
                <label style={MINI_LABEL_STYLE}>Gap (px)</label>
                <input
                  type="number" min={0} max={80}
                  value={rep.layout.gap ?? 20}
                  onChange={(e) => onChange({ ...block, repeat: { ...rep, layout: { ...rep.layout, gap: Math.max(0, Number(e.target.value) || 0) } } } as Block)}
                  style={INPUT_STYLE}
                />
              </span>
              <span style={{ flex: 1 }}>
                <label style={MINI_LABEL_STYLE}>Min width (px)</label>
                <input
                  type="number" min={80}
                  value={rep.layout.minColumnWidth ?? ''}
                  placeholder="220"
                  title="Narrowest a column may get before the grid drops a column"
                  onChange={(e) => {
                    const v = e.target.value.trim()
                    onChange({ ...block, repeat: { ...rep, layout: { ...rep.layout, minColumnWidth: v ? Math.max(40, Number(v) || 0) : undefined } } } as Block)
                  }}
                  style={INPUT_STYLE}
                />
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <span style={{ flex: 1 }}>
              <label style={MINI_LABEL_STYLE}>Show at most</label>
              <input
                type="number" min={1}
                value={rep.max ?? ''}
                placeholder="All rows"
                onChange={(e) => {
                  const v = e.target.value.trim()
                  onChange({ ...block, repeat: { ...rep, max: v ? Math.max(1, Math.floor(Number(v)) || 1) : undefined } } as Block)
                }}
                style={INPUT_STYLE}
              />
            </span>
            <span style={{ flex: 1 }}>
              <label style={MINI_LABEL_STYLE}>Canvas copies</label>
              <input
                type="number" min={1} max={12}
                value={rep.designCount ?? ''}
                placeholder="3"
                title="Placeholder copies drawn on the design canvas (it has no live rows)"
                onChange={(e) => {
                  const v = e.target.value.trim()
                  onChange({ ...block, repeat: { ...rep, designCount: v ? Math.min(12, Math.max(1, Math.floor(Number(v)) || 1)) : undefined } } as Block)
                }}
                style={INPUT_STYLE}
              />
            </span>
          </div>
          <RepeatSelectEditor block={block} rep={rep} page={page} onChange={onChange} />
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '6px 0 0' }}>
            Bind this block's fields to row columns with the <b>row field</b> binding source.
          </p>
        </div>
      )}
    </div>
  )
}

// ─── Breadcrumb editor ────────────────────────────
// The LAST item is the current page: it renders as text whatever its href,
// so the editor says so rather than letting an author wonder why their
// link does nothing.

function BreadcrumbEditor({ block, onChange }: {
  block: BreadcrumbBlock
  onChange: (b: Block) => void
}) {
  const items = block.items || []
  const upd = (next: BreadcrumbItem[]) => onChange({ ...block, items: next } as Block)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Separator</label>
        <input
          value={block.separator ?? '/'}
          onChange={(e) => onChange({ ...block, separator: e.target.value } as Block)}
          style={{ ...INPUT_STYLE, width: 80 }}
        />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Alignment</label>
        <select
          value={block.align || 'left'}
          onChange={(e) => onChange({ ...block, align: e.target.value } as Block)}
          style={INPUT_STYLE}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
      {items.map((it, i) => (
        <div key={it.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            value={it.label}
            onChange={(e) => { const n = [...items]; n[i] = { ...it, label: e.target.value }; upd(n) }}
            placeholder="Label" style={{ ...INPUT_STYLE, flex: 2 }}
          />
          <input
            value={it.href || ''}
            disabled={i === items.length - 1}
            onChange={(e) => { const n = [...items]; n[i] = { ...it, href: e.target.value || undefined }; upd(n) }}
            placeholder={i === items.length - 1 ? 'this page' : '/slug'}
            style={{ ...INPUT_STYLE, flex: 2, fontFamily: 'monospace' }}
          />
          <button
            className="btn btn-ghost btn-sm"
            onClick={(e) => { e.stopPropagation(); upd(items.filter((_, k) => k !== i)) }}
            disabled={items.length <= 1}
            aria-label="Remove step"
          >×</button>
        </div>
      ))}
      <button
        className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }}
        onClick={(e) => {
          e.stopPropagation()
          upd([...items, { id: `bc${Date.now().toString(36)}`, label: 'Step' }])
        }}
      >Add step</button>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem' }}>
        <input
          type="checkbox"
          checked={block.structuredData !== false}
          onChange={(e) => onChange({ ...block, structuredData: e.target.checked } as Block)}
        />
        Emit BreadcrumbList structured data
      </label>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        The last step is the current page — it renders as text, not a link.
      </p>
    </div>
  )
}

// ─── Nav menu editor ──────────────────────────────
// A single authored navigation entry — the schema's own NavMenuItem, so the
// editor can't drift from it again (a local copy lacked the mega-menu
// description / icon / badge / featured fields).
type NavMenuItemT = NavMenuItem

const NAV_SELECT_STYLE: React.CSSProperties = {
  padding: '6px 8px', fontSize: 13, border: '1px solid var(--color-border)',
  borderRadius: 4, background: 'var(--color-bg)', color: 'var(--color-text)',
}
const NAV_INPUT_STYLE = NAV_SELECT_STYLE

// Flatten the site menu's nav folders (recursively) into options for the
// "menu root folder" dropdown. The value is the folder's obj_name when
// known (what the renderer's findMenuRoot matches first), falling back to
// its label; indentation shows nesting depth.
function collectNavFolders(
  items: SiteMenuItem[] | undefined,
  depth = 0,
  out: { value: string; label: string }[] = [],
): { value: string; label: string }[] {
  for (const it of items || []) {
    if ((it as any).type === 'group') {
      const g = it as any
      const value = (g.obj_name || g.label || '').trim()
      if (value) out.push({ value, label: `${'  '.repeat(depth)}${g.label || value}` })
      collectNavFolders(g.children, depth + 1, out)
    }
  }
  return out
}

// Seed a custom menu from the live site navigation so the user starts from
// the real structure instead of a blank slate. Pages become links to their
// slug; folders become parent items carrying their children.
function siteMenuToNavItems(items: SiteMenuItem[] | undefined): NavMenuItemT[] {
  return (items || []).map((it) => {
    const node = it as any
    if (node.type === 'group') {
      return { id: newId('nav'), label: node.label || 'Menu', children: siteMenuToNavItems(node.children) }
    }
    return { id: newId('nav'), label: node.label || node.page_obj_name || 'Page', href: '/' + (node.slug || '') }
  })
}

// ─── Custom nav drag-and-drop ─────────────────────
// Entries carry unique ids, so a drop is two id-keyed tree edits on the ROOT
// list: take the dragged entry out, put it back relative to the target
// (before / after it, inside it as its last sub-item, or at the end of a
// list). One drag state is shared by every nesting level through context,
// so an entry can move between levels, not only within its own list.
type NavDropPos = 'before' | 'after' | 'inside'
interface NavDragCtx {
  dragId: string | null
  setDragId: (id: string | null) => void
  /** Move entry `dragId` relative to `targetId`; `targetId` null + 'inside'
      with `parentId` = append to that list (null parentId = top level). */
  drop: (targetId: string | null, pos: NavDropPos, parentId?: string | null) => void
}
const NavDragContext = createContext<NavDragCtx | null>(null)

function navTake(items: NavMenuItemT[], id: string): [NavMenuItemT[], NavMenuItemT | null] {
  let taken: NavMenuItemT | null = null
  const walk = (list: NavMenuItemT[]): NavMenuItemT[] => list.flatMap((it) => {
    if (it.id === id) { taken = it; return [] }
    if (!it.children?.length) return [it]
    const ch = walk(it.children)
    return [ch === it.children ? it : { ...it, children: ch.length ? ch : undefined }]
  })
  const next = walk(items)
  return [next, taken]
}

function navContains(node: NavMenuItemT, id: string): boolean {
  return (node.children || []).some((c) => c.id === id || navContains(c, id))
}

function navFind(items: NavMenuItemT[], id: string): NavMenuItemT | null {
  for (const it of items) {
    if (it.id === id) return it
    const hit = navFind(it.children || [], id)
    if (hit) return hit
  }
  return null
}

function navInsert(
  items: NavMenuItemT[], node: NavMenuItemT, targetId: string | null, pos: NavDropPos, parentId: string | null,
): NavMenuItemT[] {
  // Append to a list (end-of-list drop zone).
  if (targetId === null) {
    if (!parentId) return [...items, node]
    return items.map((it) => it.id === parentId
      ? { ...it, children: [...(it.children || []), node] }
      : (it.children?.length ? { ...it, children: navInsert(it.children, node, null, pos, parentId) } : it))
  }
  const idx = items.findIndex((it) => it.id === targetId)
  if (idx >= 0) {
    if (pos === 'inside') {
      return items.map((it, i) => i === idx ? { ...it, children: [...(it.children || []), node] } : it)
    }
    const next = items.slice()
    next.splice(pos === 'before' ? idx : idx + 1, 0, node)
    return next
  }
  return items.map((it) => it.children?.length
    ? { ...it, children: navInsert(it.children, node, targetId, pos, parentId) }
    : it)
}

// Recursive editor for a list of custom navigation entries. Each row edits a
// label + link and can hold its own nested sub-items, giving an arbitrarily
// deep (multi-level) navigation structure. Rows reorder with ↑/↓ or by
// dragging the grip — onto another entry's top/bottom edge to sit before /
// after it, onto its middle to become its sub-item.
function CustomNavList(props: {
  items: NavMenuItemT[]
  onChange: (items: NavMenuItemT[]) => void
  depth?: number
  mega?: boolean
  parentId?: string | null
}) {
  const outer = useContext(NavDragContext)
  const [dragId, setDragId] = useState<string | null>(null)
  if (outer) return <CustomNavListInner {...props} />
  // Top level — owns the drag state and applies drops to the whole tree.
  const { items, onChange } = props
  const drop: NavDragCtx['drop'] = (targetId, pos, parentId = null) => {
    try {
      if (!dragId || dragId === targetId) return
      const dragged = navFind(items, dragId)
      if (!dragged) return
      // An entry cannot be dropped into its own sub-items.
      if (targetId && navContains(dragged, targetId)) return
      if (!targetId && parentId && (parentId === dragId || navContains(dragged, parentId))) return
      const [without, moved] = navTake(items, dragId)
      if (!moved) return
      onChange(navInsert(without, moved, targetId, pos, parentId))
    } catch (err) {
      console.error('[nav_menu] drag-and-drop move failed', err)
    } finally {
      setDragId(null)
    }
  }
  return (
    <NavDragContext.Provider value={{ dragId, setDragId, drop }}>
      <CustomNavListInner {...props} />
    </NavDragContext.Provider>
  )
}

function CustomNavRow({
  id, children, style,
}: { id: string; children: React.ReactNode; style: React.CSSProperties }) {
  const dnd = useContext(NavDragContext)
  const [over, setOver] = useState<NavDropPos | null>(null)
  const active = !!dnd?.dragId && dnd.dragId !== id
  const outline: React.CSSProperties = over === 'inside'
    ? { boxShadow: 'inset 0 0 0 2px var(--color-accent)' }
    : over === 'before' ? { boxShadow: '0 -2px 0 0 var(--color-accent)' }
    : over === 'after' ? { boxShadow: '0 2px 0 0 var(--color-accent)' }
    : {}
  return (
    <div
      onDragOver={(ev) => {
        if (!active) return
        ev.preventDefault()
        ev.stopPropagation()
        const r = ev.currentTarget.getBoundingClientRect()
        const y = ev.clientY - r.top
        // Edges place the entry beside this one; the middle nests it.
        const edge = Math.min(24, r.height * 0.3)
        setOver(y < edge ? 'before' : y > r.height - edge ? 'after' : 'inside')
      }}
      onDragLeave={(ev) => {
        if (!ev.currentTarget.contains(ev.relatedTarget as Node)) setOver(null)
      }}
      onDrop={(ev) => {
        ev.preventDefault()
        ev.stopPropagation()
        const pos = over
        setOver(null)
        if (active && pos) dnd!.drop(id, pos)
      }}
      style={{ ...style, ...outline, opacity: dnd?.dragId === id ? 0.45 : 1 }}
    >
      {children}
    </div>
  )
}

function CustomNavListInner({
  items,
  onChange,
  depth = 0,
  mega = false,
  parentId = null,
}: {
  items: NavMenuItemT[]
  onChange: (items: NavMenuItemT[]) => void
  depth?: number
  mega?: boolean
  parentId?: string | null
}) {
  const dnd = useContext(NavDragContext)
  const update = (id: string, patch: Partial<NavMenuItemT>) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)))
  const remove = (id: string) => onChange(items.filter((it) => it.id !== id))
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir
    if (j < 0 || j >= items.length) return
    const next = items.slice()
    ;[next[idx], next[j]] = [next[j], next[idx]]
    onChange(next)
  }
  const addChild = (id: string) =>
    onChange(items.map((it) => (it.id === id
      ? { ...it, children: [...(it.children || []), { id: newId('nav'), label: 'New item', href: '' }] }
      : it)))

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {items.map((it, idx) => (
        <CustomNavRow
          key={it.id}
          id={it.id}
          style={{
            border: '1px solid var(--color-border)', borderRadius: 6, padding: 8,
            display: 'grid', gap: 6,
            background: depth ? 'var(--color-surface)' : 'transparent',
          }}
        >
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                draggable
                onDragStart={(ev) => {
                  ev.stopPropagation()
                  ev.dataTransfer.effectAllowed = 'move'
                  // Firefox starts a drag only when data is set.
                  try { ev.dataTransfer.setData('text/plain', it.id) } catch { /* not fatal */ }
                  dnd?.setDragId(it.id)
                }}
                onDragEnd={() => dnd?.setDragId(null)}
                title="Drag to reorder — drop on the middle of an entry to make it a sub-item"
                style={{ cursor: 'grab', color: 'var(--color-text-secondary)', display: 'inline-flex', flexShrink: 0 }}
              >
                <GripVertical size={14} />
              </span>
              <input
                type="text"
                value={it.label}
                placeholder="Label"
                onChange={(e) => update(it.id, { label: e.target.value })}
                style={{ ...NAV_INPUT_STYLE, flex: 1, minWidth: 0 }}
              />
            </div>
            <input
              type="text"
              value={it.href || ''}
              placeholder="Link — /slug or https://…"
              onChange={(e) => update(it.id, { href: e.target.value || undefined })}
              style={NAV_INPUT_STYLE}
            />
            {/* Mega-menu extras — rendered only in the mega layout, but a
                saved value always shows so it can be edited or cleared. */}
            {(mega || it.description || it.icon || it.badge) && (
              <>
                <input
                  type="text"
                  value={it.description || ''}
                  placeholder="Description (mega menu)"
                  onChange={(e) => update(it.id, { description: e.target.value || undefined })}
                  style={NAV_INPUT_STYLE}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                  <input
                    type="text"
                    value={it.icon || ''}
                    placeholder="Icon name"
                    onChange={(e) => update(it.id, { icon: e.target.value || undefined })}
                    style={NAV_INPUT_STYLE}
                  />
                  <input
                    type="text"
                    value={it.badge || ''}
                    placeholder="Badge, e.g. New"
                    onChange={(e) => update(it.id, { badge: e.target.value || undefined })}
                    style={NAV_INPUT_STYLE}
                  />
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(idx, -1)} disabled={idx === 0}>↑</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}>↓</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => addChild(it.id)}>
              <Plus size={12} /> Sub-item
            </button>
            <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)' }} onClick={() => remove(it.id)}>
              <Trash2 size={12} />
            </button>
          </div>
          {it.children && it.children.length > 0 && (
            <div style={{ borderLeft: '2px solid var(--color-border)', paddingLeft: 8, marginLeft: 2 }}>
              <CustomNavList
                items={it.children}
                depth={depth + 1}
                mega={mega}
                parentId={it.id}
                onChange={(ch) => update(it.id, { children: ch.length ? ch : undefined })}
              />
            </div>
          )}
        </CustomNavRow>
      ))}
      {/* Add button doubles as the end-of-list drop zone for THIS level. */}
      <div
        onDragOver={(ev) => { if (dnd?.dragId) { ev.preventDefault(); ev.stopPropagation() } }}
        onDrop={(ev) => { ev.preventDefault(); ev.stopPropagation(); dnd?.drop(null, 'inside', parentId) }}
        style={{ justifySelf: 'stretch', minHeight: 24 }}
      >
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onChange([...items, { id: newId('nav'), label: 'New item', href: '' }])}
        >
          <Plus size={12} /> Add menu item
        </button>
      </div>
    </div>
  )
}

function NavMenuEditor({
  block,
  siteMenu,
  siteMenus,
  siteEditUrl,
  onChange,
}: {
  block: any
  siteMenu?: SiteMenuItem[]
  siteMenus?: SiteMenus
  siteEditUrl?: string
  onChange: (b: any) => void
}) {
  const source: 'site' | 'custom' = block.source === 'custom' ? 'custom' : 'site'
  const folders = useMemo(() => collectNavFolders(siteMenu), [siteMenu])
  const menuNames = useMemo(() => Object.keys(siteMenus || {}), [siteMenus])
  const items: NavMenuItemT[] = Array.isArray(block.items) ? block.items : []

  // Toggle the navigation source. Switching to custom for the first time
  // seeds the entries from the live site navigation (rooted at the chosen
  // folder, if any) so the user edits a real multi-level structure rather
  // than starting empty.
  const setSource = (useCustom: boolean) => {
    if (useCustom) {
      const seed = items.length
        ? items
        : siteMenuToNavItems(block.menu ? (siteMenus?.[block.menu] || []) : rootScopedMenu(siteMenu, block.root))
      onChange({ ...block, source: 'custom', items: seed })
    } else {
      onChange({ ...block, source: 'site' })
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10, fontSize: 13 }}>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        {source === 'site'
          ? "Shows one of this website's menus (header, footer …), or every page when no menu is picked. Place it in the site header or footer page; links resolve correctly on every surface."
          : 'Define your own multi-level navigation. Each entry has a label and a link; add sub-items to build dropdown submenus to any depth.'}
      </p>

      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: 'var(--color-text)' }}>
        <input
          type="checkbox"
          checked={source === 'custom'}
          onChange={(e) => setSource(e.target.checked)}
        />
        Use a custom navigation entry (instead of the system navigation)
      </label>

      <label style={{ display: 'grid', gap: 4 }}>
        <span style={{ color: 'var(--color-text-secondary)' }}>Alignment</span>
        <select
          value={block.align || 'left'}
          onChange={(e) => onChange({ ...block, align: e.target.value })}
          style={NAV_SELECT_STYLE}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </label>

      {/* Mobile drawer + dropdown layout — the runtime's defaults are a
          right-hand drawer below 768px and a plain list dropdown. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>On small screens</span>
          <select
            value={block.mobile || 'drawer'}
            onChange={(e) => onChange({ ...block, mobile: e.target.value === 'drawer' ? undefined : e.target.value })}
            style={NAV_SELECT_STYLE}
          >
            <option value="drawer">Collapse to a menu button</option>
            <option value="none">Keep the links (wrap)</option>
            {block.mobile && block.mobile !== 'drawer' && block.mobile !== 'none' && <option value={block.mobile}>{block.mobile} (unrecognised)</option>}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>Collapse below (px)</span>
          <input
            type="number" min={320}
            value={block.mobileBreakpoint ?? ''}
            placeholder="768"
            onChange={(e) => onChange({ ...block, mobileBreakpoint: e.target.value.trim() ? Number(e.target.value) : undefined })}
            style={NAV_INPUT_STYLE}
          />
        </label>
      </div>
      {(block.mobile || 'drawer') === 'drawer' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Drawer opens from</span>
            <select
              value={block.drawerSide || 'right'}
              onChange={(e) => onChange({ ...block, drawerSide: e.target.value })}
              style={NAV_SELECT_STYLE}
            >
              <option value="right">Right</option>
              <option value="left">Left</option>
              <option value="full">Full screen</option>
            </select>
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Button label (a11y)</span>
            <input
              value={block.drawerLabel || ''}
              placeholder="Menu"
              onChange={(e) => onChange({ ...block, drawerLabel: e.target.value || undefined })}
              style={NAV_INPUT_STYLE}
            />
          </label>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>Dropdowns</span>
          <select
            value={block.dropdownLayout || 'list'}
            onChange={(e) => onChange({ ...block, dropdownLayout: e.target.value })}
            style={NAV_SELECT_STYLE}
          >
            <option value="list">List</option>
            <option value="mega">Mega menu (columns)</option>
          </select>
        </label>
        {block.dropdownLayout === 'mega' && (
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Mega columns</span>
            <select
              value={String(block.megaColumns ?? 3)}
              onChange={(e) => onChange({ ...block, megaColumns: Number(e.target.value) })}
              style={NAV_SELECT_STYLE}
            >
              <option value="2">2</option><option value="3">3</option><option value="4">4</option>
            </select>
          </label>
        )}
      </div>

      {source === 'site' ? (
        <>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: 'var(--color-text)' }}>
            <input
              type="checkbox"
              checked={block.includeHome !== false}
              onChange={(e) => onChange({ ...block, includeHome: e.target.checked })}
            />
            Include Home link
          </label>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Menu</span>
            <select
              value={block.menu || ''}
              onChange={(e) => {
                const v = e.target.value
                const next = { ...block, menu: v || undefined }
                // A named menu replaces the legacy folder pin outright.
                if (v) delete next.root
                onChange(next)
              }}
              style={NAV_SELECT_STYLE}
            >
              <option value="">{block.root ? `Folder “${block.root}” (legacy)` : 'Whole site (every page)'}</option>
              {menuNames.map((n) => (
                <option key={n} value={n}>{n === 'header' ? 'Header menu' : n === 'footer' ? 'Footer menu' : n}</option>
              ))}
              {block.menu && !menuNames.includes(block.menu) && (
                <option value={block.menu}>{block.menu} (not defined — shows no links)</option>
              )}
            </select>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              The website’s menus are set in the{' '}
              {siteEditUrl
                ? <a href={siteEditUrl} target="_blank" rel="noreferrer">website settings</a>
                : 'website settings'}
              {menuNames.length ? '.' : ' — this website has none yet.'}
            </span>
          </label>
          {/* LEGACY: pin to a nav folder. Only offered while a block still
              uses it, so new sites are never steered into folder menus. */}
          {block.root && !block.menu && (
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Menu root folder (legacy)</span>
            <select
              value={block.root || ''}
              onChange={(e) => { const v = e.target.value; onChange({ ...block, root: v || undefined }) }}
              style={NAV_SELECT_STYLE}
            >
              <option value="">Whole site (every page)</option>
              {folders.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
              {/* Preserve a previously-typed root even if it no longer
                  resolves to a known folder, so it isn't silently dropped. */}
              {block.root && !folders.some((f) => f.value === block.root) && (
                <option value={block.root}>{block.root} (not found)</option>
              )}
            </select>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              This menu is built from a folder in the navigation tree. Convert the
              website to named menus in its settings, or pick a Menu above.
            </span>
          </label>
          )}
        </>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={{ color: 'var(--color-text-secondary)' }}>Navigation entries</span>
          <CustomNavList items={items} mega={block.dropdownLayout === 'mega'} onChange={(next) => onChange({ ...block, items: next })} />
          {items.length === 0 && (
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
              No entries yet — add menu items to build your navigation.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

// Return the slice of the site menu under the chosen root folder (matched by
// obj_name then label, the same precedence the renderer uses), or the whole
// menu when no root is set / found. Used to seed a custom menu sensibly.
function rootScopedMenu(siteMenu: SiteMenuItem[] | undefined, root?: string): SiteMenuItem[] {
  if (!siteMenu || !root) return siteMenu || []
  const target = root.trim().toLowerCase()
  let found: SiteMenuItem[] | null = null
  const walk = (items: SiteMenuItem[]) => {
    for (const it of items) {
      if (found) return
      const g = it as any
      if (g.type === 'group') {
        if ((g.obj_name || '').trim().toLowerCase() === target
          || (g.label || '').trim().toLowerCase() === target) {
          found = g.children || []
          return
        }
        walk(g.children || [])
      }
    }
  }
  walk(siteMenu)
  return found || siteMenu
}

// ─── Block editor ─────────────────────────────────

function BlockEditor({
  project,
  block,
  page,
  transactions,
  siteMenu,
  siteMenus,
  siteEditUrl,
  onChange,
  onDelete,
  onOpenTransaction,
}: {
  project: string
  block: Block
  page: PageModel
  transactions: { obj_name: string; title: string }[]
  siteMenu?: SiteMenuItem[]
  siteMenus?: SiteMenus
  siteEditUrl?: string
  onChange: (b: Block) => void
  onDelete: () => void
  onOpenTransaction?: (txnObjName: string) => void
}) {
  // Two-tab layout — Properties holds the type-specific data fields (the
  // things that change with block.type) plus the block id, while Layout
  // holds cross-cutting positioning + appearance (size, padding/margin/
  // background/border, visibility, animation) that exist for every block.
  // Reset to Properties when switching to a different block so the user
  // always lands on the type-specific fields first.
  const [tab, setTab] = useState<'props' | 'layout'>('props')
  useEffect(() => { setTab('props') }, [block.id])

  // "Use theme default" — restore every visual field on this block to
  // what `makeBlock(block.type)` ships by default, then drop the shared
  // `style` bag (per-block padding/margin/border/shadow/radius/align
  // overrides). The previous implementation cleared `background` /
  // `textColor` to undefined, which on blocks like Hero/CTA/Container
  // left the block without ITS OWN background so the page bg showed
  // through — the user's intent is "give me the block's factory look,"
  // not "remove the block's background." Pulling from `makeBlock`
  // means a hero gets its gradient back, a CTA gets its accent fill, a
  // container gets its surface color, etc.
  const blockRecord = block as unknown as Record<string, unknown>
  // Visual fields that live directly on the block (not in `style`).
  // Anything in this list is replaced with the factory default; if the
  // factory doesn't set it, it's removed.
  const VISUAL_FIELDS = ['background', 'textColor', 'padding', 'rounded', 'shadow', 'align'] as const
  const factory = makeBlock(block.type) as unknown as Record<string, unknown>
  const hasFactoryOverride = VISUAL_FIELDS.some(k =>
    k in blockRecord && !isDeepEqual(blockRecord[k], factory[k]))
  const hasThemeOverride = !!block.style || hasFactoryOverride

  const resetToThemeDefault = () => {
    const next: Record<string, unknown> = { ...blockRecord, style: undefined }
    for (const k of VISUAL_FIELDS) {
      if (k in factory) next[k] = factory[k]
      else if (k in blockRecord) delete next[k]
    }
    onChange(next as unknown as Block)
  }

  function isDeepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true
    if (a == null || b == null) return false
    if (typeof a !== 'object' || typeof b !== 'object') return false
    const ak = Object.keys(a as object)
    const bk = Object.keys(b as object)
    if (ak.length !== bk.length) return false
    for (const k of ak) {
      if (!isDeepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false
    }
    return true
  }

  return (
    <div style={SECTION_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <h3 style={sectionHeader}>Block · {block.type}</h3>
        <button onClick={onDelete} className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)' }}>
          <Trash2 size={13} /> Delete
        </button>
      </div>

      <BlockTabs tab={tab} setTab={setTab} />

      {tab === 'props' && <>
      <BlockIdEditor block={block} page={page} onChange={onChange} />

      {/* Repeat this block's template once per row of a list data source. */}
      <RepeatControl block={block} page={page} onChange={onChange} />

      {/* Bind a block's collection setting to a page variable (typically
          a `type:"json"` variable populated by an API). Whenever the
          variable changes the runtime re-renders the block with the
          new collection — see vision-ang-runtime/smart-page/src/app/
          core/binding-resolver.service.ts. */}
      <CollectionBindingsSection block={block} page={page} project={project} transactions={transactions} onChange={onChange} />

      {/* Per-field (scalar) bindings: wire title/text/url/etc. to a data
          source or page variable without editing JSON. */}
      <FieldBindingsSection block={block} page={page} project={project} transactions={transactions} onChange={onChange} />

      {block.type === 'heading' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Level</label>
          <select
            value={block.level}
            onChange={(e) => onChange({ ...block, level: Number(e.target.value) as 1 | 2 | 3 })}
            style={INPUT_STYLE}
          >
            <option value={1}>H1</option>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </select>
          {/* The small-caps label above the heading. It was only available
              on hero/feature_grid/steps, so a section led by a plain
              heading had its eyebrow hand-rolled as a styled paragraph. */}
          <label style={{ ...FIELD_LABEL_STYLE, marginTop: 8 }}>Eyebrow</label>
          <input
            value={block.eyebrow || ''}
            onChange={(e) => onChange({ ...block, eyebrow: e.target.value || undefined })}
            placeholder="e.g. Our range"
            style={INPUT_STYLE}
          />
        </div>
      )}

      {block.type === 'breadcrumb' && (
        <BreadcrumbEditor block={block} onChange={onChange} />
      )}

      {(block.type === 'bullet_list' || block.type === 'numbered_list') && (
        <p style={{
          margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5,
        }}>
          Press Enter on a list item to add a new one. Tab indents and Shift+Tab outdents.
          Hover an item to delete it.
        </p>
      )}

      {block.type === 'code' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Language</label>
          <input
            value={block.language || ''}
            onChange={(e) => onChange({ ...block, language: e.target.value })}
            style={INPUT_STYLE}
            placeholder="e.g. javascript, python"
          />
        </div>
      )}

      {block.type === 'image' && <ImageEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'video' && <VideoEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'gallery' && <GalleryEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'map' && <MapEditor block={block} onChange={onChange} />}
      {block.type === 'logo_cloud' && <LogoCloudEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'hero' && <HeroEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'cta_banner' && <CtaBannerEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'spacer' && <SpacerEditor block={block} onChange={onChange} />}
      {block.type === 'columns' && <ColumnsEditor block={block} onChange={onChange} />}
      {block.type === 'container' && <ContainerEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'card' && <CardEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'card_grid' && <CardGridEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'feature_grid' && <FeatureGridEditor block={block} onChange={onChange} />}
      {block.type === 'stats' && <StatsEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'countdown' && <CountdownEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'timer' && <TimerEditor block={block} onChange={onChange} />}
      {block.type === 'testimonial' && <TestimonialEditor block={block} project={project} onChange={onChange} />}
      {block.type === 'pricing' && <PricingEditor block={block} onChange={onChange} />}
      {block.type === 'faq' && <FaqEditor block={block} onChange={onChange} />}
      {block.type === 'tabs' && <TabsEditor block={block} onChange={onChange} />}
      {block.type === 'accordion' && <AccordionEditor block={block} onChange={onChange} />}
      {block.type === 'timeline' && <TimelineEditor block={block} onChange={onChange} />}
      {block.type === 'callout' && <CalloutEditor block={block} onChange={onChange} />}
      {block.type === 'button' && <ButtonEditor block={block} onChange={onChange} />}
      {block.type === 'button_group' && <ButtonGroupEditor block={block} onChange={onChange} />}
      {(() => {
        // Marketing sections (bento, marquee, counter, team, steps, …): one
        // editor per type, registered in marketingBlockEditors.tsx.
        const Editor = MARKETING_BLOCK_EDITORS[block.type]
        return Editor ? (
          <CollapsibleSection title={MARKETING_EDITOR_TITLES[block.type] || block.type} defaultOpen>
            <Editor block={block} project={project} onChange={onChange} />
          </CollapsibleSection>
        ) : null
      })()}
      {block.type === 'newsletter' && <NewsletterEditor block={block} project={project} transactions={transactions} onChange={onChange} />}
      {block.type === 'contact_form' && <ContactFormEditor block={block} project={project} transactions={transactions} onChange={onChange} />}

      {block.type === 'input_group' && (
        <InputGroupEditor block={block} page={page} onChange={onChange} />
      )}

      {block.type === 'embed' && (
        <EmbedEditor
          project={project}
          block={block}
          page={page}
          transactions={transactions}
          onChange={onChange}
          onOpenTransaction={onOpenTransaction}
        />
      )}

      {block.type === 'file_upload' && (
        <CollapsibleSection title="File upload" defaultOpen>
          <FileUploadEditor block={block} page={page} onChange={onChange} />
        </CollapsibleSection>
      )}

      {/* Type-specific deep editors — kept on the Content tab because
          they configure the block's data, not its presentation. */}
      {block.type === 'form' && (
        <CollapsibleSection title="Form fields" defaultOpen>
          <FormBlockEditor block={block as any} onChange={onChange as any}
            project={project} transactions={transactions} />
        </CollapsibleSection>
      )}
      {block.type === 'custom_code' && (
        <CollapsibleSection title="Custom code" defaultOpen>
          <CustomCodeEditor block={block as any} onChange={onChange as any} />
        </CollapsibleSection>
      )}
      {block.type === 'custom_html' && (
        <CollapsibleSection title="Custom HTML" defaultOpen>
          <CustomCodeEditor block={block as any} onChange={onChange as any} />
        </CollapsibleSection>
      )}
      {block.type === 'nav_menu' && (
        <CollapsibleSection title="Navigation menu" defaultOpen>
          <NavMenuEditor block={block as any} siteMenu={siteMenu} siteMenus={siteMenus} siteEditUrl={siteEditUrl} onChange={onChange as any} />
        </CollapsibleSection>
      )}
      {block.type === 'table' && (
        <CollapsibleSection title="Table" defaultOpen>
          <TableBlockEditor block={block as any} onChange={onChange as any} />
        </CollapsibleSection>
      )}
      {block.type === 'variable' && (
        <CollapsibleSection title="Variable display" defaultOpen>
          <VariableBlockEditor block={block as any} page={page} onChange={onChange as any} />
        </CollapsibleSection>
      )}
      {block.type === 'chatbot' && (
        <CollapsibleSection title="Chatbot settings" defaultOpen>
          <ChatbotBlockEditor block={block} transactions={transactions} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'auth_menu' && (
        <CollapsibleSection title="Authentication menu" defaultOpen>
          <AuthMenuEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'entity_picker' && (
        <CollapsibleSection title="Entity picker" defaultOpen>
          <EntityPickerEditor block={block} page={page} project={project} transactions={transactions} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'new_customer_form' && (
        <CollapsibleSection title="New customer form" defaultOpen>
          <NewCustomerFormEditor block={block} page={page} project={project} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'product_selection' && (
        <CollapsibleSection title="Product selection" defaultOpen>
          <ProductSelectionEditor block={block} page={page} project={project} transactions={transactions} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'product_preview' && (
        <CollapsibleSection title="Product preview" defaultOpen>
          <ProductPreviewEditor block={block} page={page} project={project} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'order_summary' && (
        <CollapsibleSection title="Order summary" defaultOpen>
          <OrderSummaryEditor block={block} page={page} project={project} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'order_submit' && (
        <CollapsibleSection title="Order submit" defaultOpen>
          <OrderSubmitEditor block={block} page={page} project={project} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'payment_checkout' && (
        <CollapsibleSection title="Payment checkout" defaultOpen>
          <PaymentCheckoutEditor block={block} transactions={transactions} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'filter_bar' && (
        <CollapsibleSection title="Filter bar" defaultOpen>
          <FilterBarEditor block={block} page={page} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'auth_form' && (
        <CollapsibleSection title="Sign-in form" defaultOpen>
          <AuthFormEditor block={block} page={page} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'cart_badge' && (
        <CollapsibleSection title="Cart badge" defaultOpen>
          <CartBadgeEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'cart_lines' && (
        <CollapsibleSection title="Cart lines" defaultOpen>
          <CartLinesEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'cart_summary' && (
        <CollapsibleSection title="Cart summary" defaultOpen>
          <CartSummaryEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'wishlist_button' && (
        <CollapsibleSection title="Wishlist button" defaultOpen>
          <WishlistButtonEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'wishlist_badge' && (
        <CollapsibleSection title="Wishlist badge" defaultOpen>
          <WishlistBadgeEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'wishlist_lines' && (
        <CollapsibleSection title="Wishlist lines" defaultOpen>
          <WishlistLinesEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {block.type === 'payment_settings' && (
        <CollapsibleSection title="Payment settings" defaultOpen>
          <PaymentSettingsEditor block={block} onChange={onChange} />
        </CollapsibleSection>
      )}
      {(block.type === 'cart_checkout' || block.type === 'my_records' || block.type === 'reviews') && (
        <CollapsibleSection title={COMMERCE_BLOCK_TITLES[block.type]} defaultOpen>
          <CommerceBlockProps block={block as Block} project={project} transactions={transactions} onChange={onChange as (b: Block) => void} />
        </CollapsibleSection>
      )}
      {(block.type === 'collection_search' || block.type === 'comments') && (
        <CollapsibleSection title={block.type === 'comments' ? 'Comments' : 'Collection search'} defaultOpen>
          <ReaderBlockProps block={block as CollectionSearchBlock | CommentsBlock}
            transactions={transactions} onChange={onChange as (b: Block) => void} />
        </CollapsibleSection>
      )}
      {block.type === 'rich_content' && (
        <CollapsibleSection title="Rich content" defaultOpen>
          <RichContentProps block={block as RichContentBlock} onChange={onChange as (b: RichContentBlock) => void} />
        </CollapsibleSection>
      )}
      {block.type === 'notepad' && (
        <CollapsibleSection title="Notepad" defaultOpen>
          <div>
            <label style={FIELD_LABEL_STYLE}>Min height (px)</label>
            <input
              type="number"
              min={40}
              max={1200}
              value={(block as any).minHeight ?? 160}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                if (!Number.isFinite(v)) return
                onChange({ ...(block as any), minHeight: v })
              }}
              style={INPUT_STYLE}
            />
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Use the formatting toolbar above the notepad on the canvas to apply
              bold/italic, headings, lists, alignment, links, and colors.
            </p>
          </div>
        </CollapsibleSection>
      )}

      {/* Document mode lives on the Properties tab — it's a per-block data
          choice (does this block accept user input on the document?), not
          a layout setting. Only meaningful when the page itself is a Smart
          Document template AND when the block type is editable / extensible. */}
      {(normalizePageKind(page).kind ?? 'internal') === 'document' && isDocumentApplicable(block) && (
        <CollapsibleSection title="Document mode" defaultOpen>
          <DocumentBlockEditor block={block} page={page} onChange={onChange} />
        </CollapsibleSection>
      )}
      </>}

      {tab === 'layout' && <>
        <p style={{
          margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5,
        }}>
          Properties shared by every block type — size &amp; position,
          background &amp; border, visibility, and entrance animation.
        </p>

        {/* "Use theme default" — one-click reset for this block's
            visual overrides. Lives at the top of the Layout tab so the
            user sees it next to the other layout/style controls it
            affects. Only renders when there's something to reset. */}
        {hasThemeOverride && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            padding: 10, borderRadius: 6,
            background: 'var(--color-bg-secondary, var(--color-bg))',
            border: '1px solid var(--color-border)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
              Reset to theme default
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
              Restores the block's factory background, text color, padding, and
              radius — and drops every per-field override below. Useful after a
              theme switch.
            </div>
            <button
              onClick={resetToThemeDefault}
              className="btn btn-ghost btn-sm"
              style={{
                alignSelf: 'flex-start', marginTop: 2,
                color: 'var(--color-accent)',
              }}
              title="Restore the block's factory visual fields and clear per-field overrides."
            >
              <Sparkles size={13} /> Use theme default
            </button>
          </div>
        )}

        {/* Width/height + padding + margin — grouped so every block shares
            the same layout controls in the same place. */}
        <SizeAndPositionEditor block={block} onChange={onChange} />

        {/* Background, border, radius, shadow, text colour, alignment.
            Padding/margin moved into SizeAndPositionEditor above. */}
        <BlockStyleEditor block={block} project={project} onChange={onChange} defaultOpen />

        <CollapsibleSection title="Visibility" defaultOpen>
          <VisibilityEditor
            value={block.visibility}
            onChange={v => onChange({ ...block, visibility: v })}
          />
        </CollapsibleSection>
        <CollapsibleSection title="Conditional style" defaultOpen={!!block.styleRules?.length}>
          <StyleRulesEditor
            value={block.styleRules}
            onChange={(styleRules) => onChange({ ...block, styleRules } as Block)}
          />
        </CollapsibleSection>
        <CollapsibleSection title="Animation" defaultOpen>
          <AnimationEditor
            value={block.animation}
            onChange={v => onChange({ ...block, animation: v })}
          />
        </CollapsibleSection>

        {/* Anchor / tooltip / whole-block click — the three properties that
            turn a presentational block into a navigable, interactive one.
            Before these, a clickable card was impossible without custom_code
            and there was no way to make a block a scroll target. */}
        {/* Opens itself when any of the three is set — a collapsed section
            gave no hint that a block was already clickable or anchored. */}
        <CollapsibleSection title="Interaction" defaultOpen={!!(block.anchorId || block.tooltip || block.on_click)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={FIELD_LABEL_STYLE}>Anchor id</label>
              <input
                value={block.anchorId ?? ''}
                onChange={e => onChange({
                  ...block,
                  // Normalise: an anchor id ends up in a URL fragment.
                  anchorId: e.target.value.trim().toLowerCase()
                    .replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '') || undefined,
                })}
                placeholder="e.g. features"
                style={{ ...INPUT_STYLE, fontFamily: 'monospace' }}
              />
              <p style={HELP_STYLE}>
                Makes this block a target for “Scroll to section” buttons and
                <code> #hash</code> links.
              </p>
            </div>

            <div>
              <label style={FIELD_LABEL_STYLE}>Tooltip</label>
              <input
                value={block.tooltip?.title ?? ''}
                onChange={e => onChange({
                  ...block,
                  tooltip: e.target.value
                    ? { ...(block.tooltip || {}), title: e.target.value }
                    : undefined,
                })}
                placeholder="Title shown on hover"
                style={INPUT_STYLE}
              />
              {block.tooltip?.title && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input
                    value={block.tooltip.body ?? ''}
                    onChange={e => onChange({
                      ...block,
                      tooltip: { ...block.tooltip!, body: e.target.value || undefined },
                    })}
                    placeholder="Detail (optional)"
                    style={{ ...INPUT_STYLE, flex: 2 }}
                  />
                  <select
                    value={block.tooltip.placement || 'top'}
                    onChange={e => onChange({
                      ...block,
                      tooltip: { ...block.tooltip!, placement: e.target.value as 'top' },
                    })}
                    style={{ ...INPUT_STYLE, flex: 1 }}
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              )}
            </div>

            <div>
              <label style={FIELD_LABEL_STYLE}>On click (whole block)</label>
              <OnClickEditor
                spec={{
                  id: `${block.id}_click`,
                  label: '',
                  variant: 'primary',
                  on_click: block.on_click,
                }}
                onChange={(next) => onChange({
                  ...block,
                  on_click: next.on_click && next.on_click.kind !== 'none' ? next.on_click : undefined,
                })}
              />
              <p style={HELP_STYLE}>
                Makes the entire block clickable — the standard “clickable
                card” pattern. A real link or button inside the block keeps its
                own behaviour.
              </p>
            </div>
          </div>
        </CollapsibleSection>
      </>}
    </div>
  )
}

// Block types that can meaningfully participate in document mode. Decorative
// blocks (hero, divider, spacer, image, etc.) hold the same content for every
// document instance, so we hide the Document mode section for them rather
// than letting the user toggle a setting that has no effect.
function isDocumentApplicable(block: Block): boolean {
  // Every block type can be marked editable in document mode EXCEPT a
  // small set that has no meaningful per-document content: pure visual
  // separators, embeds of other objects (which manage their own state),
  // raw HTML/CSS/JS blocks (security-sensitive), and read-only display
  // blocks. The runtime decides how to render each editable block; if a
  // particular block type doesn't yet have an inline editor, marking it
  // editable is a no-op rather than a hard error.
  switch (block.type) {
    case 'divider':
    case 'spacer':
    case 'embed':
    case 'custom_code':
    case 'custom_html':
    case 'nav_menu':
    case 'variable':
    case 'input_group':
      return false
    default:
      return true
  }
}

function DocumentBlockEditor({ block, page, onChange }: { block: Block; page: PageModel; onChange: (b: Block) => void }) {
  // Containers can be marked extensible so the document instance can grow
  // new rows / items / nested blocks at runtime.
  const isContainer =
    block.type === 'todo' ||
    block.type === 'bullet_list' ||
    block.type === 'numbered_list' ||
    block.type === 'table' ||
    block.type === 'columns' ||
    block.type === 'card_grid' ||
    block.type === 'tabs' ||
    block.type === 'accordion' ||
    block.type === 'container' ||
    block.type === 'card' ||
    block.type === 'feature_grid' ||
    block.type === 'timeline' ||
    block.type === 'stats' ||
    block.type === 'pricing' ||
    block.type === 'faq' ||
    block.type === 'gallery' ||
    block.type === 'logo_cloud' ||
    block.type === 'button_group' ||
    block.type === 'contact_form' ||
    block.type === 'form'

  // "runtimeAddable" — i.e. the user can append BRAND-NEW child blocks
  // (paragraph, image, etc.) inside this container at runtime — only
  // makes sense on containers that actually nest blocks (children /
  // tabs / accordion / columns / Container / Card). The collection
  // containers above (lists, pricing tiers, etc.) handle add/remove
  // through `documentExtensible` instead.
  const isBlockContainer =
    block.type === 'container' ||
    block.type === 'card' ||
    block.type === 'columns' ||
    block.type === 'tabs' ||
    block.type === 'accordion'

  const editable = !!(block as any).documentEditable
  const required = !!(block as any).documentRequired
  const extensible = !!(block as any).documentExtensible
  const runtimeAddable = !!(block as any).runtimeAddable
  const pageRuntimeAddable = page.runtimeAddable !== false  // default-true

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
        Marks this block as user-editable in document instances. Non-editable blocks
        render the same value for every document and cannot be modified at runtime.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={editable}
          onChange={(e) => onChange({ ...block, documentEditable: e.target.checked || undefined } as Block)}
        />
        <span>Editable in document mode</span>
      </label>
      {editable && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => onChange({ ...block, documentRequired: e.target.checked || undefined } as Block)}
          />
          <span>Required to save</span>
        </label>
      )}
      {editable && isContainer && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={extensible}
            onChange={(e) => onChange({ ...block, documentExtensible: e.target.checked || undefined } as Block)}
          />
          <span>Allow users to add or remove rows/items</span>
        </label>
      )}
      {isBlockContainer && (
        <label
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13,
            opacity: pageRuntimeAddable ? 1 : 0.55,
          }}
          title={pageRuntimeAddable ? '' : 'Page-level "Allow users to add blocks at runtime" must be on for this to take effect.'}
        >
          <input
            type="checkbox"
            checked={runtimeAddable}
            disabled={!pageRuntimeAddable}
            onChange={(e) => onChange({ ...block, runtimeAddable: e.target.checked || undefined } as Block)}
          />
          <span style={{ lineHeight: 1.4 }}>
            Allow users to add new blocks inside this container at runtime
            {!pageRuntimeAddable && (
              <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Enable “Allow users to add blocks at runtime” on the page first.
              </span>
            )}
          </span>
        </label>
      )}

      {/* Per-block delete opt-in. Only meaningful when the page-level
          policy is 'marked'; for any other policy the runtime ignores
          this flag, so we hint accordingly. */}
      <PerBlockDeletableToggle block={block} page={page} onChange={onChange} />
    </div>
  )
}

function PerBlockDeletableToggle({
  block, page, onChange,
}: {
  block: Block
  page: PageModel
  onChange: (b: Block) => void
}) {
  const policy: RuntimeDeletePolicy = page.runtimeDeletable ?? 'added_only'
  const enabled = !!(block as { runtimeDeletable?: boolean }).runtimeDeletable
  const policyHint = (() => {
    switch (policy) {
      case 'marked':     return ''
      case 'none':       return 'Page policy disables deletion entirely; this flag is ignored.'
      case 'added_only': return 'Page policy lets users delete only runtime-added blocks; this flag is ignored.'
      case 'empty':      return 'Page policy lets users delete empty blocks; this flag is ignored.'
      case 'any':        return 'Page policy already allows deletion of every block; this flag is redundant.'
    }
  })()
  const isMarkedPolicy = policy === 'marked'
  return (
    <label
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13,
        opacity: isMarkedPolicy ? 1 : 0.55,
      }}
      title={policyHint}
    >
      <input
        type="checkbox"
        checked={enabled}
        disabled={!isMarkedPolicy}
        onChange={(e) => onChange({ ...block, runtimeDeletable: e.target.checked || undefined } as Block)}
      />
      <span style={{ lineHeight: 1.4 }}>
        Allow users to delete this block at runtime
        {!isMarkedPolicy && (
          <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {policyHint}
          </span>
        )}
      </span>
    </label>
  )
}

function BlockTabs({ tab, setTab }: { tab: 'props' | 'layout'; setTab: (t: 'props' | 'layout') => void }) {
  const btn = (id: 'props' | 'layout'): React.CSSProperties => ({
    flex: 1, padding: '8px 10px', fontSize: 13, fontWeight: 600,
    background: 'transparent', border: 'none',
    color: tab === id ? 'var(--color-text)' : 'var(--color-text-secondary)',
    borderBottom: tab === id ? '2px solid var(--color-accent)' : '2px solid transparent',
    cursor: 'pointer', marginBottom: -1,
  })
  return (
    <div style={{
      display: 'flex',
      borderBottom: '1px solid var(--color-border)',
      margin: '0 -14px',
    }}>
      <button type="button" onClick={() => setTab('props')} style={btn('props')}>Properties</button>
      <button type="button" onClick={() => setTab('layout')} style={btn('layout')}>Layout</button>
    </div>
  )
}

function VariableBlockEditor({ block, page, onChange }: { block: any; page: PageModel; onChange: (b: any) => void }) {
  const set = (patch: Record<string, unknown>) => onChange({ ...block, ...patch })
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)' }}>
        Pick which variable to display. Define your variables in the <strong>Variables</strong> tab on the left.
      </p>
      <div>
        <label style={FIELD_LABEL_STYLE}>Variable</label>
        <select value={block.name ?? ''} onChange={(e) => set({ name: e.target.value })} style={INPUT_STYLE}>
          <option value="">— Choose a variable —</option>
          {missingOption(block.name, page.inputs.map((v) => v.name), 'not declared')}
          {page.inputs.map(v => (
            <option key={v.name} value={v.name}>
              {v.label || v.name} ({v.kind ?? 'input'})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Label (optional)</label>
        <input value={block.label ?? ''} onChange={(e) => set({ label: e.target.value })} style={INPUT_STYLE} placeholder="Order total" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Prefix</label>
          <input value={block.prefix ?? ''} onChange={(e) => set({ prefix: e.target.value })} style={INPUT_STYLE} placeholder="$" />
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Suffix</label>
          <input value={block.suffix ?? ''} onChange={(e) => set({ suffix: e.target.value })} style={INPUT_STYLE} placeholder="kg" />
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Format</label>
        <select value={block.format ?? 'auto'} onChange={(e) => set({ format: e.target.value })} style={INPUT_STYLE}>
          <option value="auto">Auto (raw)</option>
          <option value="number">Number (1,234.56)</option>
          <option value="currency">Currency ($1,234.56)</option>
          <option value="percentage">Percentage (12%)</option>
          <option value="date">Date</option>
          <option value="datetime">Date + time</option>
          <option value="time">Time</option>
          <option value="json">JSON (formatted)</option>
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <label style={FIELD_LABEL_STYLE}>Size</label>
          <select value={block.textSize ?? 'md'} onChange={(e) => set({ textSize: e.target.value })} style={INPUT_STYLE}>
            <option value="sm">Small</option>
            <option value="md">Medium</option>
            <option value="lg">Large</option>
            <option value="xl">Extra large</option>
          </select>
        </div>
        <div>
          <label style={FIELD_LABEL_STYLE}>Align</label>
          <select value={block.align ?? 'left'} onChange={(e) => set({ align: e.target.value })} style={INPUT_STYLE}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Empty placeholder</label>
        <input value={block.emptyText ?? ''} onChange={(e) => set({ emptyText: e.target.value })} style={INPUT_STYLE} placeholder="—" />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Loading text</label>
        <input value={block.loadingText ?? ''} onChange={(e) => set({ loadingText: e.target.value })} style={INPUT_STYLE} placeholder="Loading…" />
      </div>
    </div>
  )
}

function CollapsibleSection({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = React.useState(!!defaultOpen)
  return (
    <div style={{ borderTop: '1px solid var(--color-border)', padding: '10px 14px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left',
          fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.04em', color: 'var(--color-text-secondary)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        {open ? '▾' : '▸'} {title}
      </button>
      {open && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  )
}

// ─── Input group block editor ─────────────────────────────

function InputGroupEditor({
  block,
  page,
  onChange,
}: {
  block: InputGroupBlock
  page: PageModel
  onChange: (b: Block) => void
}) {
  const toggleInput = (name: string) => {
    const has = block.inputs.includes(name)
    onChange({
      ...block,
      inputs: has ? block.inputs.filter((n) => n !== name) : [...block.inputs, name],
    })
  }

  return (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Layout</label>
        {/* Unset is STACKED on every runtime (forms.tsx, Run view); the
            panel used to show "Inline" for it. */}
        <select
          value={block.layout || 'stacked'}
          onChange={(e) => onChange({ ...block, layout: e.target.value as 'inline' | 'stacked' })}
          style={INPUT_STYLE}
        >
          <option value="inline">Inline (row)</option>
          <option value="stacked">Stacked (column)</option>
          {missingOption(block.layout, ['inline', 'stacked'], 'unrecognised — renders stacked')}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Inputs in this group</label>
        {page.inputs.length === 0 ? (
          <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
            Define page inputs first (left panel → Page Inputs).
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {page.inputs.map((inp) => (
              <label key={inp.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={block.inputs.includes(inp.name)}
                  onChange={() => toggleInput(inp.name)}
                />
                <span>{inp.label || inp.name}</span>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                  ({inp.type})
                </span>
              </label>
            ))}
          </div>
        )}
        {/* Names in the group the page doesn't declare still render (as a
            plain text field) — list them so they can be seen and removed. */}
        {block.inputs.filter((n) => !page.inputs.some((i) => i.name === n)).map((n) => (
          <label key={`undeclared-${n}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9125rem', cursor: 'pointer', marginTop: 4 }}>
            <input type="checkbox" checked onChange={() => toggleInput(n)} />
            <span style={{ fontFamily: 'monospace' }}>{n}</span>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-error)' }}>(not declared — renders as text)</span>
          </label>
        ))}
      </div>
    </>
  )
}

// ─── Embed block editor ─────────────────────────────────

function EmbedEditor({
  project,
  block,
  page,
  transactions,
  onChange,
  onOpenTransaction,
}: {
  project: string
  block: EmbedBlock
  page: PageModel
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
  onOpenTransaction?: (txnObjName: string) => void
}) {
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [dashboards, setDashboards] = useState<ProjectDashboardSummary[]>([])
  const [visuals, setVisuals] = useState<ProjectVisualSummary[]>([])
  const [pages, setPages] = useState<ProjectPageSummary[]>([])
  const [reports, setReports] = useState<ProjectReportSummary[]>([])
  const [dashboardTargets, setDashboardTargets] = useState<ArgTargetOption[]>([])
  const [visualTargets, setVisualTargets] = useState<ArgTargetOption[]>([])
  const [pageTargets, setPageTargets] = useState<ArgTargetOption[]>([])
  const [reportTargets, setReportTargets] = useState<ArgTargetOption[]>([])
  const [wizards, setWizards] = useState<WizardSummary[]>([])
  const [wizardTargets, setWizardTargets] = useState<ArgTargetOption[]>([])
  const objType: EmbedObjType = block.embed.obj_type || 'T'
  // Refetch when the targeted transaction is re-saved in another tab/view.
  const txnVersion = useObjectVersion('T', project, block.embed.obj_name || '')

  // Load target transaction model to get column list (only when type=T)
  useEffect(() => {
    if (objType !== 'T' || !block.embed.obj_name) {
      setModel(null)
      return
    }
    let cancelled = false
    setLoading(true)
    loadTransactionObject(project, block.embed.obj_name)
      .then((res) => {
        if (cancelled) return
        const content = res?.content
        const m = content ? (content.transaction || content) : null
        setModel(m)
      })
      .catch(() => { if (!cancelled) setModel(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, block.embed.obj_name, objType, txnVersion])

  // Lazily fetch dashboards / visuals when the embed type asks for them.
  useEffect(() => {
    if (objType !== 'D' || !project) return
    let cancelled = false
    listProjectDashboards(project)
      .then((r) => { if (!cancelled) setDashboards(r.dashboards || []) })
      .catch(() => { if (!cancelled) setDashboards([]) })
    return () => { cancelled = true }
  }, [project, objType])

  useEffect(() => {
    if (objType !== 'V' || !project) return
    let cancelled = false
    listProjectVisuals(project)
      .then((r) => { if (!cancelled) setVisuals(r.visuals || []) })
      .catch(() => { if (!cancelled) setVisuals([]) })
    return () => { cancelled = true }
  }, [project, objType])

  useEffect(() => {
    if (objType !== 'S' || !project) return
    let cancelled = false
    listProjectPages(project)
      .then((r) => { if (!cancelled) setPages(r.pages || []) })
      .catch(() => { if (!cancelled) setPages([]) })
    return () => { cancelled = true }
  }, [project, objType])

  useEffect(() => {
    if (objType !== 'R' || !project) return
    let cancelled = false
    listProjectReports(project)
      .then((r) => { if (!cancelled) setReports(r.reports || []) })
      .catch(() => { if (!cancelled) setReports([]) })
    return () => { cancelled = true }
  }, [project, objType])

  // Load destination D/V metadata to populate argument target dropdowns.
  useEffect(() => {
    if (objType !== 'D' || !project || !block.embed.obj_name) {
      setDashboardTargets([])
      return
    }
    let cancelled = false
    loadDashboardObject(project, block.embed.obj_name)
      .then((res) => {
        if (cancelled) return
        setDashboardTargets(extractDashboardTargets(res?.content))
      })
      .catch(() => { if (!cancelled) setDashboardTargets([]) })
    return () => { cancelled = true }
  }, [project, objType, block.embed.obj_name])

  useEffect(() => {
    if (objType !== 'V' || !project || !block.embed.obj_name) {
      setVisualTargets([])
      return
    }
    let cancelled = false
    loadVisualObject(project, block.embed.obj_name)
      .then((res) => {
        if (cancelled) return
        setVisualTargets(extractVisualTargets(res?.content))
      })
      .catch(() => { if (!cancelled) setVisualTargets([]) })
    return () => { cancelled = true }
  }, [project, objType, block.embed.obj_name])

  useEffect(() => {
    if (objType !== 'S' || !project || !block.embed.obj_name) {
      setPageTargets([])
      return
    }
    let cancelled = false
    loadPageObject(project, block.embed.obj_name)
      .then((res) => {
        if (cancelled) return
        setPageTargets(extractPageTargets(res?.content))
      })
      .catch(() => { if (!cancelled) setPageTargets([]) })
    return () => { cancelled = true }
  }, [project, objType, block.embed.obj_name])

  useEffect(() => {
    if (objType !== 'R' || !project || !block.embed.obj_name) {
      setReportTargets([])
      return
    }
    let cancelled = false
    loadReportObject(project, block.embed.obj_name)
      .then((res) => {
        if (cancelled) return
        // A report's criteria are the same CriteriaRule shape a sqlmodel-mode
        // visual uses (SQLMODEL.CRITERIA.query.rules), so the visual extractor
        // surfaces exactly the prompt params the report engine binds.
        setReportTargets(extractVisualTargets(res?.content))
      })
      .catch(() => { if (!cancelled) setReportTargets([]) })
    return () => { cancelled = true }
  }, [project, objType, block.embed.obj_name])

  // Transaction Wizards (U): the project's wizards, and the picked one's
  // declared launch inputs as argument targets.
  useEffect(() => {
    if (objType !== 'U' || !project) return
    let cancelled = false
    listWizards(project)
      .then((w) => { if (!cancelled) setWizards(w) })
      .catch(() => { if (!cancelled) setWizards([]) })
    return () => { cancelled = true }
  }, [project, objType])

  useEffect(() => {
    if (objType !== 'U' || !project || !block.embed.obj_name) {
      setWizardTargets([])
      return
    }
    let cancelled = false
    loadWizard(project, block.embed.obj_name)
      .then((w) => {
        if (cancelled) return
        setWizardTargets((w?.inputs || []).filter((i) => i && i.name).map((i) => ({
          name: i.name, label: i.label || i.name,
          hint: [i.type, i.required ? 'required' : ''].filter(Boolean).join(' · ') || undefined,
        })))
      })
      .catch(() => { if (!cancelled) setWizardTargets([]) })
    return () => { cancelled = true }
  }, [project, objType, block.embed.obj_name])

  const columns: ColumnDef[] = useMemo(() => {
    if (!model) return []
    const formIdx = block.embed.form_no ? Math.max(0, parseInt(block.embed.form_no, 10) - 1) : 0
    return (model.sql_models?.[formIdx]?.sql_model.columns || []).map((c) => c.column)
  }, [model, block.embed.form_no])

  // Argument target options derived from the selected destination's
  // metadata. For T: form columns; for D/V: criteria keys (loaded above).
  const transactionTargets: ArgTargetOption[] = useMemo(
    () => columns.map((c) => ({ name: c.name, label: c.heading, hint: c.col_type })),
    [columns],
  )

  const argTargets: ArgTargetOption[] =
    objType === 'T' ? transactionTargets
    : objType === 'D' ? dashboardTargets
    : objType === 'S' ? pageTargets
    : objType === 'R' ? reportTargets
    : objType === 'U' ? wizardTargets
    : visualTargets

  const updateEmbed = (patch: Partial<typeof block.embed>) => {
    onChange({ ...block, embed: { ...block.embed, ...patch } })
  }

  const updateFilter = (filterId: string, patch: Partial<EmbedFilter>) => {
    const filters = block.embed.filters.map((f) => (f.id === filterId ? { ...f, ...patch } : f))
    updateEmbed({ filters })
  }

  const removeFilter = (filterId: string) => {
    updateEmbed({ filters: block.embed.filters.filter((f) => f.id !== filterId) })
  }

  const addFilter = () => {
    updateEmbed({ filters: [...block.embed.filters, makeFilter()] })
  }

  return (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Embed Type</label>
        <select
          value={objType}
          onChange={(e) => {
            const t = e.target.value as EmbedObjType
            // Switching type clears obj_name and filters/form to avoid stale state
            updateEmbed({ obj_type: t, obj_name: '', filters: [], form_no: undefined })
          }}
          style={INPUT_STYLE}
        >
          <option value="T">Transaction</option>
          <option value="D">Dashboard</option>
          <option value="V">Visual</option>
          <option value="R">Report</option>
          <option value="S">Smart Page</option>
          <option value="U">Transaction Wizard</option>
        </select>
      </div>

      {objType === 'T' && (
        <div>
          <ObjectPickerField
            label="Target Transaction"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name, filters: [] })}
            catalog={transactions.map((t) => ({ obj_name: t.obj_name, title: t.title })) as ObjectPickerEntry[]}
            typeLabel="transaction"
            emptyHint="No transactions in this project — pick one from the Workbench navigation, or use a custom name."
          />
          {block.embed.obj_name && onOpenTransaction && (
            <button
              type="button"
              onClick={() => onOpenTransaction(block.embed.obj_name)}
              style={{
                marginTop: 6,
                padding: '6px 10px',
                fontSize: '0.8125rem',
                background: 'var(--color-bg)',
                color: 'var(--color-accent, #4c8dff)',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              title={`Open ${block.embed.obj_name} in the transaction designer`}
            >
              <Sparkles size={12} />
              Edit transaction…
            </button>
          )}
          {loading && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              Loading model…
            </div>
          )}
          {block.embed.obj_name && !loading && !model && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              Model not yet generated. Generate it from the Workbench first to map filters.
            </div>
          )}
        </div>
      )}

      {objType === 'D' && (
        <div>
          <ObjectPickerField
            label="Target Dashboard"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name })}
            catalog={dashboards.map((d) => ({
              obj_name: d.obj_name,
              title: d.dashboard_name || d.obj_name,
              hint: d.visual_count ? `${d.visual_count} visual${d.visual_count === 1 ? '' : 's'}` : undefined,
            }))}
            typeLabel="dashboard"
            emptyHint="No dashboards in this project yet. Build one in the Dashboard designer first, or use a custom name."
          />
        </div>
      )}

      {objType === 'V' && (
        <div>
          <ObjectPickerField
            label="Target Visual"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name })}
            catalog={visuals.map((v) => ({
              obj_name: v.obj_name,
              title: v.obj_name,
              hint: v.visual_type || v.schema_name,
            }))}
            typeLabel="visual"
            emptyHint="No visuals in this project yet. Build one in the Visual designer first, or use a custom name."
          />
        </div>
      )}

      {objType === 'R' && (
        <div>
          <ObjectPickerField
            label="Target Report"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name })}
            catalog={reports.map((r) => ({
              obj_name: r.obj_name,
              title: r.title || r.obj_name,
              hint: r.description || undefined,
            }))}
            typeLabel="report"
            emptyHint="No reports in this project yet. Build one in the Report designer first, or use a custom name."
          />
        </div>
      )}

      {objType === 'U' && (
        <div>
          <ObjectPickerField
            label="Target Wizard"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name, args: [] })}
            catalog={wizards.map((w) => ({
              obj_name: w.obj_name,
              title: w.title || w.obj_name,
              hint: w.base_object ? `on ${w.base_object}` : undefined,
            }))}
            typeLabel="wizard"
            emptyHint="No wizards in this project yet. Build one in the Wizard designer first, or use a custom name."
          />
          <div style={{ marginTop: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
            The wizard runs in the page. What a visitor may do is the wizard&apos;s
            own rights — on a public page, grant it to the site-visitor role to
            let anonymous visitors use its action buttons. Steps that keep a run
            on the server ask the visitor to sign in.
          </div>
        </div>
      )}

      {objType === 'U' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>When it finishes → variable (json)</label>
          <select
            value={block.embed.resultVar || ''}
            onChange={(e) => updateEmbed({ resultVar: e.target.value || undefined })}
            style={INPUT_STYLE}
          >
            <option value="">— None —</option>
            {missingOption(block.embed.resultVar, page.inputs.filter((i) => i.type === 'json').map((i) => i.name), 'not a json variable')}
            {page.inputs
              .filter((inp) => inp.type === 'json')
              .map((inp) => (
                <option key={inp.name} value={inp.name}>
                  {inp.label || inp.name}
                </option>
              ))}
          </select>
          <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Receives {'{wizard, outcome, txn_id}'} when a run ends, so other blocks can react.
          </div>
        </div>
      )}

      {objType === 'S' && (
        <div>
          <ObjectPickerField
            label="Target Smart Page"
            value={block.embed.obj_name}
            onChange={(name) => updateEmbed({ obj_name: name })}
            // Filter out the page being edited so an author cannot embed a
            // page inside itself (the runtime also guards against cycles).
            catalog={pages
              .filter((p) => p.obj_name !== page.obj_name)
              .map((p) => ({
                obj_name: p.obj_name,
                title: p.title || p.obj_name,
                hint: p.input_count
                  ? `${p.input_count} input${p.input_count === 1 ? '' : 's'}`
                  : undefined,
              }))}
            typeLabel="smart page"
            emptyHint="No other smart pages in this project yet. Create one first, or use a custom name."
          />
          {block.embed.obj_name === page.obj_name && (
            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-error)' }}>
              A smart page cannot embed itself.
            </div>
          )}
        </div>
      )}

      {/* T-only render-mode picker. 'realtime' (default) embeds Vision's
          transaction UI; 'capture' renders forms locally from sql_models
          and posts to processTransactionRequest. */}
      {objType === 'T' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Render mode</label>
          <select
            value={block.embed.renderMode || 'realtime'}
            onChange={(e) => {
              const rm = e.target.value as 'realtime' | 'capture' | 'integrate'
              // Capture / integrate share the same wire shape and now
              // support filter+retrieve+selection. Default mode for
              // those is 'add' (processTransactionRequest distinguishes
              // add vs edit by the presence of primary_key on each
              // record). Filters survive the switch in either direction
              // since both capture and integrate honour them.
              // Mode is now orthogonal to render mode (access mode applies to
              // all render modes), so no mode coercion on switch.
              updateEmbed({ renderMode: rm })
            }}
            style={INPUT_STYLE}
          >
            <option value="realtime">Real-time (embed Vision UI)</option>
            <option value="capture">Capture data</option>
            <option value="integrate">Integrate (send to external system)</option>
          </select>
          <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
            {(() => {
              const rm = block.embed.renderMode || 'realtime'
              if (rm === 'capture') {
                return 'Renders header + detail forms from the transaction model; Save posts a batched process-transaction-request. Supports list+filter+retrieve like realtime, with multi-record batch save through the live-preview executor (real INSERT/UPDATE + validations). Use when the document owns its captured rows.'
              }
              if (rm === 'integrate') {
                return 'Same UI shape as Capture, but the batch Save target is configurable via Integration URL (default: live-preview executor). Use when the smart page is a form for an external system.'
              }
              return 'Embeds the live transaction UI from Vision (list / add / edit).'
            })()}
          </div>
        </div>
      )}

      {objType === 'T' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Access mode</label>
          <select
            value={normalizeEmbedMode(block.embed)}
            onChange={(e) => {
              const m = e.target.value as NormalizedEmbedMode
              const patch: Partial<EmbedBlock['embed']> = { mode: m }
              // Constrain the capture template to what the mode supports.
              if (m === 'single_add') patch.captureTemplate = 'single-classic'
              else if (m === 'multi_add'
                && (block.embed.captureTemplate || 'single-classic') === 'single-classic') {
                patch.captureTemplate = 'multi-tabular'
              }
              updateEmbed(patch)
            }}
            style={INPUT_STYLE}
          >
            <option value="full">Full access (browse / add / edit / delete)</option>
            <option value="single_add">Single add (one record, auto-added)</option>
            <option value="multi_add">Multiple add (add many records)</option>
            <option value="edit">Edit records (within filter scope)</option>
          </select>
          <div style={{ marginTop: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
            {(() => {
              switch (normalizeEmbedMode(block.embed)) {
                case 'single_add': return 'The visitor fills exactly one record; it is added on save. No Add button, no browsing of existing records.'
                case 'multi_add': return 'The visitor can add multiple new records. Existing records are not shown.'
                case 'edit': return 'The visitor sees and edits only records within the filter scope below. No adding or deleting header records.'
                default: return 'Full access — browse, add, edit and delete records (subject to grants).'
              }
            })()}
          </div>
        </div>
      )}

      {/* form_no: realtime mode lets the author pick ONE sql_model to
          embed; capture mode renders all sql_models (header + details)
          so the picker is hidden. */}
      {objType === 'T'
        && (block.embed.renderMode || 'realtime') === 'realtime'
        && model
        && (model.sql_models?.length || 0) > 1 && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Form #</label>
          <select
            value={block.embed.form_no || '1'}
            onChange={(e) => updateEmbed({ form_no: e.target.value })}
            style={INPUT_STYLE}
          >
            {model.sql_models.map((sm, i) => (
              <option key={i} value={String(i + 1)}>{i + 1}: {sm.sql_model.form_title || `Form ${i + 1}`}</option>
            ))}
          </select>
        </div>
      )}

      {/* Capture-mode template — single record vs multi-record layouts. */}
      {objType === 'T' && (block.embed.renderMode === 'capture' || block.embed.renderMode === 'integrate') && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Capture template</label>
          <select
            value={block.embed.captureTemplate || 'single-classic'}
            onChange={(e) => {
              const t = e.target.value as
                | 'single-classic' | 'multi-tabular' | 'multi-tab'
                | 'multi-accordion' | 'multi-form-input' | 'multi-compact'
              updateEmbed({ captureTemplate: t })
            }}
            style={INPUT_STYLE}
          >
            <option value="single-classic">Single record — classic header + details</option>
            <option value="multi-tabular">Multi-record — tabular grid (designer column layout, inline edit)</option>
            <option value="multi-tab">Multi-record — tabs (full header + details per record)</option>
            <option value="multi-accordion">Multi-record — accordion (full header + details per record)</option>
            <option value="multi-form-input">Multi-record — list + form input (popup form for add/edit)</option>
            <option value="multi-compact">Multi-record — compact cards (mobile-style list + full-screen form)</option>
          </select>
          <div style={{ marginTop: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            {(() => {
              const t = block.embed.captureTemplate || 'single-classic'
              if (t === 'single-classic') return 'One record per Save. Best when the document is about a single transaction.'
              if (t === 'multi-tabular') return 'Many records as rows in a grid laid out per the transaction designer (column widths + x-order). Add / edit happens inline in the grid. Detail rows expand into a child body.'
              if (t === 'multi-form-input') return 'Many records as a list with status badges. Add and edit open a popup form. Useful when the transaction has many columns that do not fit a single row.'
              if (t === 'multi-compact') return 'Mobile-style interface: records as cards; add/edit opens a full-screen single-column form. Best for phone-width layouts.'
              return 'Many records, each with its own header form + detail grids. All records save together via one batched processTransactionRequest call.'
            })()}
          </div>
        </div>
      )}

      {/* Bind the form to a JSON page variable: its object value pre-fills the
          form by matching keys to columns, and re-fills reactively when the
          variable changes (e.g. after a file-upload extraction action). */}
      {objType === 'T' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Pre-fill form from variable (recordVar)</label>
          <select
            value={block.embed.recordVar || ''}
            onChange={(e) => updateEmbed({ recordVar: e.target.value || undefined })}
            style={INPUT_STYLE}
          >
            <option value="">— None —</option>
            {missingOption(block.embed.recordVar, page.inputs.map((i) => i.name), 'not declared')}
            {(page.inputs || []).map((inp) => (
              <option key={inp.name} value={inp.name}>
                {inp.label || inp.name}{inp.type === 'json' ? ' (json)' : ''}
              </option>
            ))}
          </select>
          <div style={{ marginTop: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            The variable's object value seeds the add form (keys → columns). Pair with a
            file-upload block whose on-upload action writes extracted JSON into this variable.
          </div>
        </div>
      )}

      {/* Capture-mode Save button label override. */}
      {objType === 'T' && (block.embed.renderMode === 'capture' || block.embed.renderMode === 'integrate') && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Save button label</label>
          <input
            type="text"
            value={block.embed.captureSaveLabel || ''}
            placeholder="Save"
            onChange={(e) => updateEmbed({ captureSaveLabel: e.target.value || undefined })}
            style={INPUT_STYLE}
          />
        </div>
      )}

      {/* Presentation — inline vs. a modal opened by a trigger button.
          Use 'modal' for "Add feedback" / "Register" style forms that
          should not occupy the page until invoked. */}
      {(objType === 'T' || objType === 'U') && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Presentation</label>
          <select
            value={block.embed.presentation || 'inline'}
            onChange={(e) => {
              const p = e.target.value as 'inline' | 'modal'
              updateEmbed({ presentation: p === 'inline' ? undefined : p })
            }}
            style={INPUT_STYLE}
          >
            <option value="inline">Inline — render in the page</option>
            <option value="modal">Modal — button opens a dialog</option>
          </select>
          <div style={{ marginTop: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            {block.embed.presentation === 'modal'
              ? 'A trigger button is shown; the form opens in a dialog. Best paired with Capture render mode + Add for "Add feedback" / "Register" actions.'
              : 'The embed renders directly in the page flow.'}
          </div>
        </div>
      )}

      {(objType === 'T' || objType === 'U') && block.embed.presentation === 'modal' && (
        <>
          <div>
            <label style={FIELD_LABEL_STYLE}>Button label</label>
            <input
              type="text"
              value={block.embed.triggerLabel || ''}
              placeholder="Add feedback"
              onChange={(e) => updateEmbed({ triggerLabel: e.target.value || undefined })}
              style={INPUT_STYLE}
            />
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE}>Button style</label>
            <select
              value={block.embed.triggerVariant || 'primary'}
              onChange={(e) => updateEmbed({
                triggerVariant: e.target.value as 'primary' | 'secondary' | 'outline' | 'ghost',
              })}
              style={INPUT_STYLE}
            >
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
              <option value="outline">Outline</option>
              <option value="ghost">Ghost</option>
            </select>
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE}>Modal title</label>
            <input
              type="text"
              value={block.embed.modalTitle || ''}
              placeholder="Share your feedback"
              onChange={(e) => updateEmbed({ modalTitle: e.target.value || undefined })}
              style={INPUT_STYLE}
            />
          </div>
          <div>
            <label style={FIELD_LABEL_STYLE}>Success message</label>
            <input
              type="text"
              value={block.embed.successMessage || ''}
              placeholder="Thank you! Your submission was received."
              onChange={(e) => updateEmbed({ successMessage: e.target.value || undefined })}
              style={INPUT_STYLE}
            />
          </div>
        </>
      )}

      {objType === 'U' && block.embed.presentation !== 'modal' && (
        <div>
          <label style={FIELD_LABEL_STYLE}>Message when done</label>
          <input
            type="text"
            value={block.embed.successMessage || ''}
            placeholder="Done — thank you."
            onChange={(e) => updateEmbed({ successMessage: e.target.value || undefined })}
            style={INPUT_STYLE}
          />
        </div>
      )}

      {/* Grid UX — toggles + per-column totals. Applies to tabular
          template + realtime list. */}
      {objType === 'T' && isGridSurface(block.embed) && (
        <GridUxEditor
          ux={block.embed.gridUx || {}}
          totals={block.embed.columnTotals || {}}
          columns={columns}
          onUxChange={(next) => updateEmbed({ gridUx: emptyIfDefault(next) })}
          onTotalsChange={(next) => updateEmbed({
            columnTotals: Object.keys(next).length === 0 ? undefined : next,
          })}
        />
      )}

      {/* Interface switching — lets viewers swap this transaction between
          its List / Multi-tabular entry / Form-input / Compact interfaces at
          runtime (in-app live preview, published run, downloaded app). Never
          shown on the design canvas. On by default; uncheck to lock the saved
          interface. Offered for the list-level interfaces. */}
      {isSwitchableSurface(block.embed) && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: '0.8425rem', cursor: 'pointer', marginTop: 4,
          color: 'var(--color-text)',
        }}>
          <input
            type="checkbox"
            checked={block.embed.allowInterfaceSwitch !== false}
            onChange={(e) => updateEmbed({
              allowInterfaceSwitch: e.target.checked ? undefined : false,
            })}
          />
          <span>Allow viewers to switch interface (List / Multi-tabular / Form input / Compact)</span>
        </label>
      )}

      {/* T-list filters — column + op + value.
          Meaningful for:
            - realtime + mode=list (the original surface)
            - capture / integrate (any template) — drives retrieve and
              auto-refetches when a filter source variable changes,
              same as realtime. Filter-eq with a page_input value also
              seeds the corresponding column on Add. */}
      {objType === 'T'
        && (
          // Every realtime mode, not only the legacy literal 'list': the Access
          // mode select writes the normalised value ('full', 'edit', …), and
          // the runtime filters/scopes all of them.
          (block.embed.renderMode || 'realtime') === 'realtime'
          || block.embed.renderMode === 'capture'
          || block.embed.renderMode === 'integrate'
        ) && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...FIELD_LABEL_STYLE, marginBottom: 0 }}>Filters</label>
            <button
              onClick={addFilter}
              className="btn btn-ghost btn-sm"
              disabled={!block.embed.obj_name}
              title={block.embed.obj_name ? 'Add filter' : 'Pick a transaction first'}
            >
              <Plus size={13} /> Add
            </button>
          </div>

          {block.embed.filters.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              No filters. The list will show all rows.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {block.embed.filters.map((f) => (
              <FilterRow
                key={f.id}
                filter={f}
                columns={columns}
                inputs={page.inputs}
                onChange={(patch) => updateFilter(f.id, patch)}
                onRemove={() => removeFilter(f.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Row selection → page variables.
          Available for:
            - realtime + mode=list
            - capture / integrate (any template) — clicking a row in the
              capture/integrate list writes its primary_key + row data
              into these variables the same way realtime does, so
              downstream filters and {{name}} bindings react. */}
      {(
        (objType === 'T'
          && (
            (block.embed.renderMode || 'realtime') === 'realtime'
            || block.embed.renderMode === 'capture'
            || block.embed.renderMode === 'integrate'
          ))
        || objType === 'V'
      ) && (
        <div style={{
          padding: 10,
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div>
            <label style={FIELD_LABEL_STYLE}>Selection</label>
            <div style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              marginBottom: 6, lineHeight: 1.4,
            }}>
              {objType === 'V'
                ? "When the user clicks a row / card / node in this visual, write a chosen value and the full row into these page variables. Other visuals' arguments or transaction-embed filters can then react to the selection."
                : "When the user clicks a row, write the row's id and data into these page variables. Other embeds can then filter on the selected id."}
            </div>
          </div>
          {objType === 'V' && (
            <div>
              <label style={{ ...FIELD_LABEL_STYLE, fontSize: '0.8125rem' }}>
                Selected key column
              </label>
              <input
                type="text"
                value={block.embed.selectedKeyColumn || ''}
                onChange={(e) => updateEmbed({
                  selectedKeyColumn: e.target.value || undefined,
                })}
                placeholder="column name (defaults to first column)"
                style={INPUT_STYLE}
              />
              <div style={{
                marginTop: 4, fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
              }}>
                Which column of the clicked row supplies the value written to the “Selected ID” variable. Matched case-insensitively.
              </div>
            </div>
          )}
          <div>
            <label style={{ ...FIELD_LABEL_STYLE, fontSize: '0.8125rem' }}>
              Selected ID → variable
            </label>
            <select
              value={block.embed.selectedIdVar || ''}
              onChange={(e) => updateEmbed({
                selectedIdVar: e.target.value || undefined,
              })}
              style={INPUT_STYLE}
            >
              <option value="">— None —</option>
              {missingOption(block.embed.selectedIdVar, page.inputs.map((i) => i.name), 'not declared')}
              {page.inputs.map((inp) => (
                <option key={inp.name} value={inp.name}>
                  {inp.label || inp.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ ...FIELD_LABEL_STYLE, fontSize: '0.8125rem' }}>
              Selected row data → variable (json)
            </label>
            <select
              value={block.embed.selectedDataVar || ''}
              onChange={(e) => updateEmbed({
                selectedDataVar: e.target.value || undefined,
              })}
              style={INPUT_STYLE}
            >
              <option value="">— None —</option>
              {missingOption(block.embed.selectedDataVar, page.inputs.filter((i) => i.type === 'json').map((i) => i.name), 'not a json variable')}
              {page.inputs
                .filter((inp) => inp.type === 'json')
                .map((inp) => (
                  <option key={inp.name} value={inp.name}>
                    {inp.label || inp.name}
                  </option>
                ))}
            </select>
            {page.inputs.filter(i => i.type === 'json').length === 0 && (
              <div style={{
                marginTop: 4, fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)',
              }}>
                Create a JSON-type page input first to hold the selected row.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Argument mapping — pre-fills for T add/edit, criteria args for D/V,
          page-input pass-through for S. Targets come from the destination's
          own metadata (or its `inputs[]` for a smart page). */}
      {/* A realtime browse/edit list doesn't read args; every other T surface
          (add modes, capture, integrate) does. Saved args always show. */}
      {(objType !== 'T' || !isRealtimeListHost(block.embed) || (block.embed.args?.length ?? 0) > 0) && (
        <ArgMappingEditor
          title={
            objType === 'D' ? 'Dashboard arguments'
            : objType === 'V' ? 'Visual arguments'
            : objType === 'R' ? 'Report parameters'
            : objType === 'S' ? 'Page input values'
            : objType === 'U' ? 'Wizard inputs'
            : block.embed.mode === 'add' ? 'Pre-fill values (Add mode)'
            : 'Pre-fill / record-id values (Edit mode)'
          }
          args={block.embed.args}
          inputs={page.inputs}
          availableTargets={argTargets}
          metaUnavailableHint={
            !block.embed.obj_name
              ? `Pick a ${objType === 'D' ? 'dashboard' : objType === 'V' ? 'visual' : objType === 'R' ? 'report' : objType === 'S' ? 'smart page' : objType === 'U' ? 'wizard' : 'transaction'} first.`
              : argTargets.length === 0
                ? 'Destination metadata not yet available — type the target name freehand.'
                : undefined
          }
          emptyHint={
            objType === 'T'
              ? 'No pre-fills. Add a row to populate a column from a page input.'
              : objType === 'S'
                ? "No values. Add a row to seed an embedded page input from this page's variables."
              : objType === 'U'
                ? "No inputs. Add a row to start the wizard with a value from this page (a document number, a customer)."
                : `No arguments. Add a row to map a page input to a ${objType === 'D' ? 'dashboard global criterion' : objType === 'R' ? 'report prompt parameter' : 'visual criterion'}.`
          }
          project={project}
          onChange={(args) => updateEmbed({ args })}
        />
      )}
    </>
  )
}

// ─── Filter row ─────────────────────────────────

function FilterRow({
  filter,
  columns,
  inputs,
  onChange,
  onRemove,
}: {
  filter: EmbedFilter
  columns: ColumnDef[]
  inputs: PageInput[]
  onChange: (patch: Partial<EmbedFilter>) => void
  onRemove: () => void
}) {
  const setOp = (op: FilterOp) => {
    if (op === 'between') {
      onChange({
        op,
        value: undefined,
        from: filter.from || { source: 'page_input', name: '' },
        to: filter.to || { source: 'page_input', name: '' },
      })
    } else {
      onChange({
        op,
        from: undefined,
        to: undefined,
        value: filter.value || { source: 'page_input', name: '' },
      })
    }
  }

  return (
    <div style={{
      padding: 8,
      border: '1px solid var(--color-border)',
      borderRadius: 6,
      background: 'var(--color-bg)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          value={filter.column}
          onChange={(e) => onChange({ column: e.target.value })}
          style={{ ...INPUT_STYLE, flex: 1 }}
        >
          <option value="">— Column —</option>
          {missingOption(filter.column, columns.map((c) => c.name), 'not found')}
          {columns.map((c) => (
            <option key={c.name} value={c.name}>{c.heading || c.name}</option>
          ))}
        </select>
        <select
          value={filter.op}
          onChange={(e) => setOp(e.target.value as FilterOp)}
          style={{ ...INPUT_STYLE, width: 120 }}
        >
          <option value="eq">=</option>
          <option value="neq">≠</option>
          <option value="gt">&gt;</option>
          <option value="gte">≥</option>
          <option value="lt">&lt;</option>
          <option value="lte">≤</option>
          <option value="between">between</option>
          <option value="contains">contains</option>
          <option value="starts_with">starts with</option>
        </select>
        <button onClick={onRemove} className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)' }} title="Remove">
          <Trash2 size={13} />
        </button>
      </div>

      {filter.op === 'between' ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <ValueRefEditor label="From" inputs={inputs} value={filter.from} onChange={(from) => onChange({ from })} />
          <ValueRefEditor label="To"   inputs={inputs} value={filter.to}   onChange={(to)   => onChange({ to })} />
        </div>
      ) : (
        <ValueRefEditor label="Value" inputs={inputs} value={filter.value} onChange={(value) => onChange({ value })} />
      )}
    </div>
  )
}

function ValueRefEditor({
  label,
  inputs,
  value,
  onChange,
}: {
  label: string
  inputs: PageInput[]
  value: FilterValueRef | undefined
  onChange: (v: FilterValueRef) => void
}) {
  const v = value || { source: 'page_input', name: '' }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{label}</label>
      <div style={{ display: 'flex', gap: 4 }}>
        <select
          value={v.source}
          onChange={(e) => {
            const source = e.target.value as 'page_input' | 'static'
            onChange({ source, name: source === 'page_input' ? '' : undefined, value: source === 'static' ? '' : undefined })
          }}
          style={{ ...INPUT_STYLE, width: 110 }}
        >
          <option value="page_input">Page input</option>
          <option value="static">Static</option>
        </select>
        {v.source === 'page_input' ? (
          <select
            value={v.name || ''}
            onChange={(e) => onChange({ source: 'page_input', name: e.target.value })}
            style={{ ...INPUT_STYLE, flex: 1 }}
          >
            <option value="">— Pick input —</option>
            {inputs.map((inp) => (
              <option key={inp.name} value={inp.name}>{inp.label || inp.name}</option>
            ))}
          </select>
        ) : (
          <input
            value={v.value || ''}
            onChange={(e) => onChange({ source: 'static', value: e.target.value })}
            style={{ ...INPUT_STYLE, flex: 1 }}
            placeholder="Static value"
          />
        )}
      </div>
    </div>
  )
}

const sectionHeader: React.CSSProperties = {
  fontSize: '0.9125rem',
  fontWeight: 600,
  margin: 0,
  color: 'var(--color-text)',
}

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.9125rem' }}>
      {text}
    </div>
  )
}

// ─── Destination metadata extractors ─────────────────────────
// Read the saved JSON for a Dashboard or Visual and surface the keys an
// embed/navigate can pass arguments against. Matches the JSON written by
// dashboard_generator.py / visual_generator.py.

export function extractDashboardTargets(content: unknown): ArgTargetOption[] {
  if (!content || typeof content !== 'object') return []
  const dashboard = (content as Record<string, unknown>).DASHBOARD as Record<string, unknown> | undefined
  if (!dashboard) return []
  const criteria = dashboard.GLOBAL_CRITERIA as Array<Record<string, unknown>> | undefined
  if (!Array.isArray(criteria)) return []
  return criteria.map((c) => ({
    name: String(c.COLUMN_NAME || c.NAME || ''),
    label: typeof c.HEADING === 'string' ? c.HEADING : (c.LABEL as string | undefined),
    hint: typeof c.DATA_TYPE === 'string' ? c.DATA_TYPE : undefined,
  })).filter((o) => o.name)
}

// Smart-page targets are the embedded/linked page's `inputs[]` — each
// PageInput becomes a destination an outer page input can be mapped to.
// The saved page JSON is `{ page: { inputs: [...], ... } }`.
export function extractPageTargets(content: unknown): ArgTargetOption[] {
  if (!content || typeof content !== 'object') return []
  const root = content as Record<string, unknown>
  const page = (root.page as Record<string, unknown>) || root
  const inputs = page.inputs as Array<Record<string, unknown>> | undefined
  if (!Array.isArray(inputs)) return []
  return inputs.map((inp) => ({
    name: String(inp.name || ''),
    label: typeof inp.label === 'string' ? (inp.label as string) : undefined,
    hint: typeof inp.type === 'string' ? (inp.type as string) : undefined,
  })).filter((o) => o.name)
}

// True for embed surfaces that render the shared grid (tabular template
// + any realtime list). Single-classic / multi-tab / multi-accordion
// templates use forms instead of a grid, so the toolbar/totals don't
// apply there.
// A saved value a select's option list doesn't carry (an undeclared
// variable, a column the loaded model lacks) is still listed, flagged, so the
// select shows the truth instead of its first option over the real value.
function missingOption(value: string | undefined, known: string[], note: string) {
  if (!value || known.includes(value)) return null
  return <option value={value}>{value} ({note})</option>
}

// A realtime embed that renders the list host. The legacy 'list' value and
// the canonical modes the Access select writes ('full', 'edit', 'multi_add')
// all land there — gating on the literal 'list' hid the grid/switch/filter
// settings the moment an author touched the Access select.
function isRealtimeListHost(embed: EmbedBlock['embed']): boolean {
  if ((embed.renderMode || 'realtime') !== 'realtime') return false
  const m = normalizeEmbedMode(embed)
  return m === 'full' || m === 'edit' || m === 'multi_add'
}

function isGridSurface(embed: EmbedBlock['embed']): boolean {
  if (embed.obj_type !== 'T') return false
  const rm = embed.renderMode || 'realtime'
  if (isRealtimeListHost(embed)) return true
  if ((rm === 'capture' || rm === 'integrate')
      && (embed.captureTemplate || 'single-classic') === 'multi-tabular') {
    return true
  }
  return false
}

// The three list-level interfaces the runtime View switcher swaps between
// (must mirror EmbedPreview's routing into EmbedListInterfaceHost). The
// "Allow viewers to switch interface" toggle is offered only for these.
function isSwitchableSurface(embed: EmbedBlock['embed']): boolean {
  if (embed.obj_type !== 'T') return false
  const rm = embed.renderMode || 'realtime'
  if (isRealtimeListHost(embed)) return true
  if (rm === 'capture' || rm === 'integrate') {
    const t = embed.captureTemplate
    if (t === 'multi-tabular' || t === 'multi-form-input' || t === 'multi-compact') return true
  }
  return false
}

// Collapse an all-default ux object to undefined so we don't bloat the
// saved page JSON with empty trees.
function emptyIfDefault(ux: EmbedGridUx): EmbedGridUx | undefined {
  const keys: (keyof EmbedGridUx)[] = ['search', 'filter', 'sort', 'fullScreen', 'totals', 'keyboardNav']
  for (const k of keys) {
    if (ux[k] === false) return ux
  }
  return undefined
}

interface GridUxEditorProps {
  ux: EmbedGridUx
  totals: Record<string, EmbedColumnTotal>
  columns: ColumnDef[]
  onUxChange: (next: EmbedGridUx) => void
  onTotalsChange: (next: Record<string, EmbedColumnTotal>) => void
}

function GridUxEditor({ ux, totals, columns, onUxChange, onTotalsChange }: GridUxEditorProps) {
  const [editing, setEditing] = useState<ColumnDef | null>(null)
  const toggle = (key: keyof EmbedGridUx, on: boolean) => {
    // `on === true` means the feature is enabled. We store `false`
    // explicitly to override the default-on behaviour, and clear the
    // key when the user re-enables (the default).
    const next: EmbedGridUx = { ...ux }
    if (on) delete next[key]
    else next[key] = false
    onUxChange(next)
  }

  const totalsEntries = Object.entries(totals)
  const editingKey = editing ? columnDbName(editing) : ''
  const current = editingKey ? (totals[editingKey] ?? null) : null

  return (
    <div style={{
      padding: 10, background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)', borderRadius: 6,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div>
        <label style={FIELD_LABEL_STYLE}>Grid features</label>
        <div style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          marginBottom: 6, lineHeight: 1.4,
        }}>
          Toggle the grid's built-in toolbar features. All are on by default.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <UxToggle label="Search" enabled={ux.search !== false}
            onChange={(v) => toggle('search', v)} />
          <UxToggle label="Filter" enabled={ux.filter !== false}
            onChange={(v) => toggle('filter', v)} />
          <UxToggle label="Sort" enabled={ux.sort !== false}
            onChange={(v) => toggle('sort', v)} />
          <UxToggle label="Full screen" enabled={ux.fullScreen !== false}
            onChange={(v) => toggle('fullScreen', v)} />
          <UxToggle label="Totals row" enabled={ux.totals !== false}
            onChange={(v) => toggle('totals', v)} />
          <UxToggle label="Keyboard nav" enabled={ux.keyboardNav !== false}
            onChange={(v) => toggle('keyboardNav', v)} />
        </div>
      </div>

      <div>
        <label style={FIELD_LABEL_STYLE}>Column totals</label>
        <div style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          marginBottom: 6, lineHeight: 1.4,
        }}>
          Set a footer total per numeric column. Use a simple aggregate
          (SUM/AVG/MIN/MAX/COUNT) or an Excel-like expression.
        </div>
        {columns.length === 0 ? (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            Pick a transaction first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {columns.map((c) => {
              const key = columnDbName(c)
              const t = totals[key]
              return (
                <div key={key} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '4px 8px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 4,
                  background: t ? 'var(--color-bg)' : 'transparent',
                  fontSize: '0.8125rem',
                }}>
                  <span style={{ flex: 1, color: 'var(--color-text)' }}>
                    {c.heading || c.name}
                  </span>
                  <span style={{
                    color: t ? 'var(--color-accent, #6366f1)' : 'var(--color-text-secondary)',
                    fontFamily: t ? 'var(--font-mono, monospace)' : undefined,
                    maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }} title={t ? (t.kind === 'aggregate' ? t.spec.toUpperCase() : t.spec) : ''}>
                    {t ? (t.kind === 'aggregate' ? t.spec.toUpperCase() : t.spec) : '—'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(c)}
                    style={{ padding: '2px 6px' }}
                  >
                    {t ? 'Edit' : 'Set'}
                  </button>
                </div>
              )
            })}
          </div>
        )}
        {totalsEntries.length > 0 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onTotalsChange({})}
            style={{ marginTop: 6, fontSize: '0.8125rem', color: '#b91c1c' }}
          >
            <Trash2 size={11} /> Clear all totals
          </button>
        )}
      </div>

      {editing && (
        <ColumnTotalEditor
          open={true}
          column={editing}
          columnKey={editingKey}
          columns={columns}
          columnKeyOf={columnDbName}
          current={current}
          previewRows={[]}
          onSave={(next) => {
            onTotalsChange({ ...totals, [editingKey]: next })
          }}
          onClear={() => {
            const { [editingKey]: _drop, ...rest } = totals
            onTotalsChange(rest)
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function UxToggle({ label, enabled, onChange }: {
  label: string
  enabled: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: '0.8125rem', cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: 4,
      background: enabled ? 'rgba(99,102,241,0.06)' : 'transparent',
    }}>
      <input
        type="checkbox"
        checked={enabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span style={{ color: enabled ? 'var(--color-text)' : 'var(--color-text-secondary)' }}>
        {label}
      </span>
    </label>
  )
}

export function extractVisualTargets(content: unknown): ArgTargetOption[] {
  // Reuse the dashboard designer's prompt-variable extractor so the mapping
  // dropdown surfaces params from every visual authoring style: SQLMODEL
  // criteria rules, transaction-style sql_models[].criteria, AND raw-SQL
  // ?.<var> placeholders (the old impl only read SQLMODEL rules).
  return extractVisualParams(content).map((p) => ({
    name: p.name,
    label: p.promptLabel,
    hint: p.operator && p.operator !== '=' ? `${p.dataType} · ${p.operator}` : p.dataType,
  }))
}




// ── Rich content (a RICHTEXT column rendered on the page) ──────────────────
function RichContentProps({ block, onChange }: {
  block: RichContentBlock
  onChange: (b: RichContentBlock) => void
}) {
  const set = (patch: Partial<RichContentBlock>) => onChange({ ...block, ...patch })
  const tocValue = block.toc === true ? 'always' : block.toc === 'auto' ? 'auto' : 'off'
  const hint: React.CSSProperties = { margin: '4px 0 0', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }
  const check = (label: string, value: boolean, patch: (v: boolean) => Partial<RichContentBlock>) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 8 }}>
      <input type="checkbox" checked={value} onChange={e => set(patch(e.target.checked))} />
      {label}
    </label>
  )
  return (
    <div>
      <label style={FIELD_LABEL_STYLE}>Content</label>
      <input
        value={block.source || ''}
        placeholder="{{item.body}}"
        onChange={e => set({ source: e.target.value })}
        style={{ ...INPUT_STYLE, fontFamily: 'ui-monospace, Menlo, monospace' }}
      />
      <p style={hint}>
        Bind a <b>Rich Text</b> column: <code>{'{{item.body}}'}</code> on a page that shows one
        record per URL, or <code>{'{{source.column}}'}</code> for a record data source.
      </p>

      <label style={{ ...FIELD_LABEL_STYLE, marginTop: 10 }}>Table of contents</label>
      <select
        value={tocValue}
        onChange={e => set({ toc: e.target.value === 'always' ? true : e.target.value === 'auto' ? 'auto' : false })}
        style={INPUT_STYLE}
      >
        <option value="off">Off</option>
        <option value="auto">When there are 3+ headings</option>
        <option value="always">Always</option>
      </select>
      {tocValue !== 'off' && (
        <input
          value={block.tocTitle || ''}
          placeholder="Contents"
          onChange={e => set({ tocTitle: e.target.value })}
          style={{ ...INPUT_STYLE, marginTop: 6 }}
        />
      )}

      <label style={{ ...FIELD_LABEL_STYLE, marginTop: 10 }}>Typography</label>
      <select
        value={block.typography || 'article'}
        onChange={e => set({ typography: e.target.value as RichContentBlock['typography'] })}
        style={INPUT_STYLE}
      >
        <option value="article">Article — comfortable reading</option>
        <option value="compact">Compact</option>
      </select>

      {check('Readable line length (~72 characters)', block.readableWidth !== false, v => ({ readableWidth: v }))}
      {check('Open links to other sites in a new tab', !!block.externalLinksNewTab, v => ({ externalLinksNewTab: v }))}
      {check('Mark links to other sites “nofollow”', !!block.nofollowExternal, v => ({ nofollowExternal: v }))}

      <label style={{ ...FIELD_LABEL_STYLE, marginTop: 10 }}>When empty, show</label>
      <input
        value={block.emptyText || ''}
        placeholder="(nothing)"
        onChange={e => set({ emptyText: e.target.value })}
        style={INPUT_STYLE}
      />
    </div>
  )
}


// Search box / comments over a website collection. The collection is a
// transaction published on the site (Advanced → Website content); comments
// additionally need "Reader comments" switched on there.
function ReaderBlockProps({ block, transactions, onChange }: {
  block: CollectionSearchBlock | CommentsBlock
  transactions: { obj_name: string; title: string }[]
  onChange: (b: Block) => void
}) {
  const set = (patch: Record<string, unknown>) => onChange({ ...block, ...patch } as Block)
  const text = (label: string, key: string, placeholder = '') => (
    <>
      <label style={{ ...FIELD_LABEL_STYLE, marginTop: 10 }}>{label}</label>
      <input
        value={String((block as unknown as Record<string, unknown>)[key] ?? '')}
        placeholder={placeholder}
        onChange={e => set({ [key]: e.target.value })}
        style={INPUT_STYLE}
      />
    </>
  )
  return (
    <div>
      <label style={FIELD_LABEL_STYLE}>Collection</label>
      <select value={block.collection || ''} onChange={e => set({ collection: e.target.value })} style={INPUT_STYLE}>
        <option value="">— choose —</option>
        {transactions.length > 0 && missingOption(block.collection, transactions.map((t) => t.obj_name), 'not in this project')}
        {transactions.map(t => <option key={t.obj_name} value={t.obj_name}>{t.title || t.obj_name}</option>)}
      </select>
      {block.type === 'collection_search' ? (
        <>
          {text('Placeholder', 'placeholder', 'Search…')}
          {text('When nothing matches', 'emptyText', 'Nothing matches that search.')}
          <label style={{ ...FIELD_LABEL_STYLE, marginTop: 10 }}>Most results</label>
          <input type="number" min={1} max={50} value={block.limit ?? 12}
            onChange={e => set({ limit: Math.max(1, Math.min(50, Number(e.target.value) || 12)) })}
            style={INPUT_STYLE} />
        </>
      ) : (
        <>
          {text('Record', 'recordSlug', '{{item._slug}}')}
          {text('Heading', 'heading', 'Comments')}
          {text('Form heading', 'formHeading', 'Leave a comment')}
          {text('When there are none', 'emptyText', 'No comments yet.')}
          {text('Button label', 'submitLabel', 'Post comment')}
        </>
      )}
    </div>
  )
}
